import { billingDe } from "./billing.de";
import { hrDe } from "./hr.de";
import { dashboardDe } from "./dashboard.de";
import { paymentsDe } from "./payments.de";
import { reportsDe } from "./reports.de";
import type { MessageId } from "./fr";

/** German catalog. Same keys and {placeholders} as fr.ts (checked by catalogs.t.ts). */
// Terms: feature = Funktion, feature enablement = Freischaltung, role = Rolle, case (dossier) = Fall, pro = Profi, client = Kunde, job request (demande) = Anfrage, lead (opportunité) = Lead, reason (motif) = Begründung, reveal (afficher) = Anzeigen, staff member = Teammitglied, sign out = Abmelden, audit log = Audit-Protokoll
export const de: Record<MessageId, string> = {
  ...reportsDe,
  ...paymentsDe,
  ...dashboardDe,
  ...billingDe,
  ...hrDe,
  // ── Brand and shell ──
  "brand.name": "Walliyo",
  "brand.initial": "W",
  "brand.console": "Konsole",
  "nav.aria": "Konsole",
  "nav.new": "NEU",
  "nav.group.configuration": "Konfiguration",
  "overview.eachDepartment": "Jede Abteilung, eine Zeile",
  "overview.dept.marketplace": "Marktbetrieb",
  "overview.dept.growth": "Vertrieb & Wachstum",
  "overview.dept.support": "Kundendienst",
  "overview.dept.trust": "Vertrauen & Sicherheit",
  "overview.dept.finance": "Finanzen",
  "overview.dept.people": "Personalwesen",
  "overview.dept.administration": "Verwaltung",
  "overview.dept.product": "Technik",
  "overview.clickThrough": "zum Durchklicken",
  "overview.numberToWatch": "Die Zahl, auf die es ankommt",
  "overview.whereWalliyoWorks": "Wo Walliyo arbeitet",
  "overview.noCitiesYet": "Diesen Monat keine Aufträge.",
  "overview.points": "{points} Punkte",
  "overview.title.people.walliyoMonth": "Walliyo, {month}",
  "overview.people.tab.waiting": "Was Sie erwartet",
  "overview.people.tab.company": "Das Unternehmen",
  "overview.people.tab.weeks": "Die kommenden Wochen",
  "overview.people.askForTimeOff": "Urlaub beantragen",
  "overview.people.joinedThisMonth": "{count} diesen Monat dazugekommen",
  "overview.people.joinedThisMonth.one": "1 diesen Monat dazugekommen",
  "overview.people.candidates": "{count} Bewerber",
  "overview.people.candidates.one": "1 Bewerber",
  "overview.people.medianStay": "Jahre, mediane Dauer",
  "overview.people.whereEverybodyIs": "Wo alle sind",
  "overview.people.oneLinePerTeam": "eine Zeile pro Team",
  "overview.people.noTeamsYet": "Noch keine Teams.",
  "overview.people.noLead": "keine Leitung",
  "overview.people.nobodyCovering": "Niemand deckt ab",
  "overview.people.thinWeek": "{team} verliert {away} von {size} in der Woche ab {week}.",
  "overview.people.whoIsWhere": "Wer ist wo",
  "overview.people.fourWeeks": "vier Wochen, und was in jede fällt",
  "overview.people.allTimeOff": "Alle Abwesenheiten",
  "overview.people.quietWeeks": "In den nächsten vier Wochen fällt nichts an.",
  "overview.people.mark.payrollCloses": "Lohnabrechnung schließt {on}",
  "overview.people.mark.reviewsClose": "Beurteilungen schließen {on}",
  "overview.people.span.leave": "{days} Tage frei",
  "overview.people.span.leave.one": "1 Tag frei",
  "overview.people.span.leavePending": "{days} Tage · offen",
  "overview.people.span.leavePending.one": "1 Tag · offen",
  "overview.people.span.starts": "fängt an",
  "overview.people.task.candidates": "{count} Bewerber warten auf Sie",
  "overview.people.detail.candidates": "auf {second} offene Stellen",
  "marketplace.seeAll": "Alle {count} ansehen",
  "marketplace.findAPro": "Profi finden",
  "marketplace.theMarket": "Der Markt, diese Woche",
  "overview.title.marketplace.theMarket": "Der Markt, diese Woche",
  "overview.lede.marketplace.nobodyAnswered": "{first} Personen haben gefragt und niemand hat geantwortet.",
  "overview.lede.marketplace.demandNoSupply": "{first} Gewerke werden gefragt, wo kein Profi die Fähigkeit hat.",
  "overview.lede.marketplace.quiet": "Diese Woche wurde nichts eingestellt.",
  "overview.lede.marketplace.allAnswered": "Jeder Auftrag dieser Woche hat ein Angebot erhalten.",
  "overview.people.link.employees": "Beschäftigte",
  "overview.people.link.recruiting": "Personalsuche",
  "overview.people.link.learning": "Weiterbildung",
  "overview.people.link.teams": "Teams",
  "overview.administration.askedNotAnswered": "Gefragt, nicht beantwortet",
  "overview.administration.oldestFirst": "das Älteste zuerst",
  "overview.administration.nobodyWaiting": "Niemand wartet auf eine Antwort.",
  "overview.administration.row.wantsUntil": "{subject} möchte {asked}",
  "overview.administration.row.wantsForever": "{subject} möchte {asked}, ohne Enddatum",
  "overview.administration.until": "bis {on}",
  "overview.administration.noEndAsked": "kein Enddatum gewünscht",
  "overview.administration.hoursWaiting": "{hours} Stunden Wartezeit",
  "overview.administration.hoursWaiting.one": "1 Stunde Wartezeit",
  "overview.administration.pastTheDeadline": "Frist überschritten",
  "overview.administration.nobodyTookItBack": "Niemand hat es zurückgenommen",
  "overview.administration.noEndDate": "{count} Rechte haben kein Enddatum.",
  "overview.administration.noEndDate.one": "1 Recht hat kein Enddatum.",
  "overview.administration.belongToLeavers": "{leavers} gehören jemandem, der gegangen ist.",
  "overview.administration.belongToLeavers.one": "1 gehört jemandem, der gegangen ist.",
  "overview.administration.reviewThem": "Prüfen",
  "overview.administration.criticalCapabilities": "Kritische Befugnisse",
  "overview.administration.ifAnyReachesOne":
    "Fällt eine davon auf eine Person, kann niemand deren Abwesenheit abdecken.",
  "overview.figure.holdCritical": "Halten etwas Kritisches",
  "overview.lede.administration.waitingAndGrants":
    "{first} Personen warten auf eine Antwort, und {second} Rechte haben kein Enddatum.",
  "overview.lede.administration.waiting": "{first} Personen warten auf eine Antwort.",
  "overview.lede.administration.grants": "{first} Rechte haben kein Enddatum und niemand hat sie zurückgenommen.",
  "overview.lede.administration.nothingWaiting": "Niemand wartet auf eine Antwort.",
  "overview.growth.step.signedUp": "Registriert",
  "overview.growth.step.verified": "Geprüft",
  "overview.growth.step.addedSkill": "Gewerk hinzugefügt",
  "overview.growth.step.quotedOnce": "Einmal geboten",
  "overview.growth.step.wonAJob": "Auftrag gewonnen",
  "overview.growth.lost.signedUp": "nie registriert",
  "overview.growth.lost.verified": "haben aufgegeben",
  "overview.growth.lost.addedSkill": "haben es nie getan",
  "overview.growth.lost.quotedOnce": "haben nie geboten",
  "overview.growth.lost.wonAJob": "haben nie gewonnen",
  "overview.growth.stuck": "Feststeckend, und einen Anruf wert",
  "overview.growth.stuckWho": "geprüft, in einem gefragten Gewerk, nie geboten",
  "overview.growth.nobodyStuck": "Niemand steckt fest.",
  "overview.growth.row.workGoingUnanswered": "{subject}",
  "overview.growth.why.workGoingUnanswered": "vor {days} Tagen geprüft · Arbeit in ihrem Gewerk bleibt unbeantwortet",
  "overview.growth.row.neverQuoted": "{subject}",
  "overview.growth.why.neverQuoted": "vor {days} Tagen geprüft · hat noch nie geboten",
  "overview.growth.promoCost":
    "Sie hat {given} an Rabatten gegeben gegenüber {earned} an Gebühren von den Profis, die sie genutzt haben.",
  "overview.growth.promoUsed": "{used} Profis haben sie genutzt",
  "overview.growth.beforeYouCampaign": "Bevor Sie eine Kampagne starten",
  "overview.growth.notificationsAreFree":
    "Mitteilungen in der App erreichen diese Profis kostenlos. SMS kostet pro Nachricht, und Daten werden hier stundenweise bezahlt.",
  "overview.action.call": "Anrufen",
  "overview.lede.growth.neverQuoted":
    "{first} Profis haben sich registriert und nie auf etwas geboten. Das ist die ganze Aufgabe.",
  "overview.lede.growth.nobodySignedUp": "Diesen Monat hat sich niemand registriert.",
  "overview.lede.growth.allWorking": "Jeder registrierte Profi hat auf etwas geboten.",
  "overview.support.dueInMinutes.one": "Frist in 1 Minute",
  "overview.support.dueInHours.one": "Frist in 1 Stunde",
  "overview.support.overdue.one": "Frist um 1 Minute überschritten",
  "overview.support.nobodyOwnsWhy.one":
    "Ein Fall ohne Zuständigen ist ein Fall, den niemand beantwortet. Der älteste liegt seit 1 Tag.",
  "overview.people.days.one": "1 Tag",
  "overview.people.payrollOpen.one": "{month} ist offen. Er schließt morgen.",
  "overview.people.detail.leave.one": "Vor 1 Tag beantragt · Sie führen diese Person",
  "overview.finance.waiting.one": "1 Tag Wartezeit",
  "overview.trust.row.appealWaiting.one": "Einspruch wartet seit 1 Tag",
  "overview.trust.legalHoldsWhy.one":
    "1 Konto ist gesperrt. Nichts davon darf gelöscht werden, auch nicht auf Datenanfrage.",
  "overview.product.unconfirmed.one": "1 Rückmeldung unbestätigt, vor {minutes} Minuten.",
  "overview.product.whatIsBroken": "Was kaputt ist",
  "overview.product.worstFirst": "das Schlimmste zuerst",
  "overview.product.nothingBroken": "Nichts ist kaputt.",
  "overview.product.unconfirmed": "{count} Rückmeldungen unbestätigt, die älteste vor {minutes} Minuten.",
  "overview.product.unconfirmedWhy": "Jede ist ein Profi, der bezahlt hat und nicht als bezahlt geführt wird.",
  "overview.product.replayThem": "Erneut abspielen",
  "overview.product.rolledOut": "Bei Kunden ausgerollt",
  "overview.product.tally.everybody": "Alle",
  "overview.product.tally.someAccounts": "Einzelne Konten",
  "overview.product.tally.off": "Aus",
  "overview.figure.rolloutsInFlight": "Laufende Rollouts",
  "overview.product.row.incidentOpen": "{subject}",
  "overview.product.why.incidentOpen": "seit {amount} offen · Kunden sehen das Banner",
  "overview.product.row.ruleScheduled": "Regel wird {at} wirksam",
  "overview.product.why.ruleScheduled": "{subject} {amount} · niemand hat freigegeben",
  "overview.product.row.experimentRunning": "{subject} bei {days} % der Kunden",
  "overview.product.why.experimentRunning": "läuft seit {on} · entscheidungsreif",
  "overview.figure.incidentsOpen": "Offene Vorfälle",
  "overview.lede.product.incidentAndCallbacks":
    "{first} Vorfall ist offen, und {second} Zahlungsrückmeldungen sind fehlgeschlagen.",
  "overview.lede.product.incident": "{first} Vorfall ist offen.",
  "overview.lede.product.callbacks":
    "{first} Zahlungsrückmeldungen sind fehlgeschlagen und niemand hat sie erneut abgespielt.",
  "overview.lede.product.rulesScheduled": "{first} Regeln werden ohne Anwesende wirksam.",
  "overview.lede.product.nothingBroken": "Nichts ist kaputt.",
  "overview.finance.aboveLimit": "Über jemandes Grenze",
  "overview.finance.theyAskedYouDecide": "sie haben gefragt, Sie entscheiden",
  "overview.finance.nothingWaitingHere": "Keine Freigabe steht aus.",
  "overview.finance.theirLimit": "Grenze {ceiling}",
  "overview.finance.noLimit": "hält keine Grenze",
  "overview.finance.waiting": "{days} Tage Wartezeit",
  "overview.finance.today": "heute",
  "overview.finance.subscriptions": "Abonnements",
  "overview.finance.tally.endingSoon": "Erneuerung in 7 Tagen",
  "overview.finance.tally.suspended": "Zahlung fehlgeschlagen",
  "overview.finance.tally.ended": "Beendet, nicht erneuert",
  "overview.finance.promosRunning": "Laufende Aktionen",
  "overview.finance.promoEnds": "{name} endet am {on}.",
  "overview.finance.promoNoEnd": "{name} hat kein Enddatum.",
  "overview.finance.promoCost": "Sie hat {given} an Rabatten gegeben gegenüber {earned} an Gebühren.",
  "overview.figure.feesCollected": "Eingenommene Gebühren",
  "overview.figure.jobsBilled": "Abgerechnete Aufträge",
  "overview.figure.renewalsFailed": "Fehlgeschlagene Erneuerungen",
  "overview.lede.finance.waitingOnApproval": "{first} Korrekturen warten auf jemanden, der sie freigeben kann.",
  "overview.lede.finance.renewalsFailed": "{first} Erneuerungen sind fehlgeschlagen und niemand hat sie aufgegriffen.",
  "overview.lede.finance.renewingSoon": "{first} Abonnements erneuern sich noch diese Woche.",
  "overview.lede.finance.nothingWaiting": "Keine Freigabe steht aus.",
  "overview.trust.needsDecision": "Heute zu entscheiden",
  "overview.trust.mostSeriousFirst": "das Schwerste zuerst",
  "overview.trust.nothingUrgentHere": "Heute ist nichts zu entscheiden.",
  "overview.trust.verificationQueue": "Prüfwarteschlange",
  "overview.trust.whyVerify": "Ein Profi, der auf die Prüfung wartet, ist nicht zu finden und nicht zu beauftragen.",
  "overview.trust.tally.underADay": "Wartet unter 24 h",
  "overview.trust.tally.overADay": "Über 24 h",
  "overview.trust.legalHolds": "Gesetzliche Sperren",
  "overview.trust.legalHoldsWhy":
    "{count} Konten sind gesperrt. Nichts davon darf gelöscht werden, auch nicht auf Datenanfrage.",
  "overview.trust.row.criticalUnreferred": "{subject}",
  "overview.trust.why.criticalUnreferred": "kritisch · nicht an die Polizei weitergegeben",
  "overview.trust.row.restrictionExpiring": "Die Sperre endet am {on} von selbst",
  "overview.trust.why.restrictionExpiring": "{subject} · heben Sie sie auf, oder sie hebt sich selbst auf",
  "overview.trust.row.appealWaiting": "Einspruch wartet seit {days} Tagen",
  "overview.trust.why.appealWaiting": "ein gesperrtes Konto bat um erneute Prüfung",
  "overview.figure.underLegalHold": "Unter gesetzlicher Sperre",
  "overview.lede.trust.criticalUnreferred": "{first} Meldungen sind kritisch und keine wurde weitergegeben.",
  "overview.lede.trust.waitingOverADay": "{first} Profis warten seit über einem Tag auf die Prüfung.",
  "overview.lede.trust.waitingToVerify": "{first} Profis warten auf die Prüfung.",
  "overview.lede.trust.nothingUrgent": "Heute ist nichts zu entscheiden.",
  "overview.support.runningOut": "Die Zeit läuft",
  "overview.support.soonestFirst": "nächste Frist zuerst",
  "overview.support.nothingDue": "Keine Frist steht kurz bevor.",
  "overview.support.whereFrom": "Woher sie kamen",
  "overview.support.noChannels": "Keine offenen Fälle.",
  "overview.support.channel.chat": "In der App",
  "overview.support.channel.phone": "Telefon",
  "overview.support.channel.email": "E-Mail",
  "overview.support.nobodyOwns": "{count} ohne Zuständigen",
  "overview.support.nobodyOwnsWhy":
    "Ein Fall ohne Zuständigen ist ein Fall, den niemand beantwortet. Der älteste liegt seit {days} Tagen.",
  "overview.support.takeOldest": "Den ältesten übernehmen",
  "overview.support.row.mine": "Ihrer",
  "overview.support.row.theirs": "der von jemand anderem",
  "overview.support.row.unclaimed": "ohne Zuständigen",
  "overview.support.dueInMinutes": "Frist in {minutes} Minuten",
  "overview.support.dueInHours": "Frist in {hours} Stunden",
  "overview.support.overdue": "Frist um {minutes} Minuten überschritten",
  "overview.figure.toFirstReply": "Bis zur ersten Antwort",
  "overview.action.reply": "Antworten",
  "overview.action.claim": "Übernehmen",
  "overview.lede.support.breaching": "{first} Fälle überschreiten bald ihre Frist.",
  "overview.lede.support.breachingSomeYours": "{first} Fälle überschreiten bald ihre Frist. {second} davon sind Ihre.",
  "overview.lede.support.unclaimed": "{first} Fälle warten darauf, dass jemand sie übernimmt.",
  "overview.lede.support.nothingBreaching": "Keine Frist wird bald überschritten.",
  "overview.against": "gegenüber {month}",
  "overview.title.executive.walliyoMonth": "Walliyo, {month}",
  "overview.title.people.thingsNeedYou": "{count} Dinge brauchen Sie",
  "overview.title.people.nothingNeedsYou": "Nichts braucht Sie",
  "overview.title.support.bonjour": "Hallo, {name}",
  "overview.title.trust.safetyToday": "Sicherheit, heute",
  "overview.title.finance.theMoney": "Das Geld, diese Woche",
  "overview.title.product.themOrUs": "Liegt es an ihnen oder an uns",
  "overview.title.growth.gettingProsWorking": "Profis in Arbeit bringen",
  "overview.title.administration.whoMayDoWhat": "Wer darf was",
  "overview.title.marketplace.plain": "Übersicht",
  "overview.title.legal.plain": "Übersicht",
  "overview.lede.executive.grewFaster": "Der Markt wuchs. Was er nicht bedienen konnte, wuchs schneller.",
  "overview.lede.executive.wentUnanswered": "{first} Anfragen blieben diesen Monat unbeantwortet.",
  "overview.lede.executive.quiet": "Diesen Monat wurde nichts eingestellt.",
  "overview.lede.executive.holding": "{second} Anfragen eingestellt, und jede fand eine Antwort.",
  "overview.people.allClear": "Nichts braucht Sie. Alles ist erledigt.",
  "overview.people.theCompany": "Das Unternehmen",
  "overview.people.employees": "Beschäftigte",
  "overview.people.teams": "Teams",
  "overview.people.yours": "Ihres",
  "overview.people.leaveLeft": "Resturlaub",
  "overview.people.days": "{days} Tage",
  "overview.people.noAllowance": "nicht festgelegt",
  "overview.people.coursesDue": "Fällige Kurse",
  "overview.people.payroll": "Lohnabrechnung",
  "overview.people.payrollOpen": "{month} ist offen. Er schließt in {days} Tagen.",
  "overview.people.payrollDue": "{month} ist offen und sein Abschlusstermin ist verstrichen.",
  "overview.people.payrollClosed": "{month} ist abgeschlossen.",
  "overview.people.linesChecked": "{checked} von {total} Zeilen geprüft",
  "overview.people.task.leave": "{subject} möchte {count} Tage frei",
  "overview.people.detail.leave": "Vor {days} Tagen beantragt · Sie führen diese Person",
  "overview.people.task.grantsNoEndDate": "{count} Rechte haben kein Enddatum",
  "overview.people.detail.grantsNoEndDate": "{second} gehören Personen, die Sie führen",
  "overview.people.task.reviews": "{count} Beurteilungen sind von Ihnen zu schreiben",
  "overview.people.detail.reviews": "schließt am {on}",
  "overview.people.task.starting": "{subject} fängt am {on} an",
  "overview.people.detail.starting": "Rolle bereits zugewiesen",
  "overview.people.task.startingNoRole": "{subject} fängt am {on} an",
  "overview.people.detail.startingNoRole": "noch keine Rolle zugewiesen",
  "overview.action.decide": "Entscheiden",
  "overview.action.review": "Prüfen",
  "overview.action.write": "Schreiben",
  "overview.action.setUp": "Einrichten",
  "overview.action.open": "Öffnen",
  "overview.hero.jobsPosted": "Aufträge eingestellt",
  "overview.hero.gotAQuote": "Mit Angebot",
  "overview.hero.fees": "Gebühren, GNF",
  "overview.hero.activePros": "Aktive Anbieter",
  "overview.hero.employees": "Mitarbeitende",
  "overview.headline.unanswered": "{count} Aufträge ohne Gebot · {second} Gewerke ohne Abdeckung",
  "overview.headline.neverQuoted": "{count} Anbieter registriert und nie geboten",
  "overview.headline.breaching": "{count} Fälle kurz vor Frist · {second} ohne Inhaber",
  "overview.headline.criticalUnreferred": "{count} kritische Meldungen nicht übergeben · {second} Prüfungen offen",
  "overview.headline.approvalsWaiting": "{count} Korrekturen offen · {second} überfällig",
  "overview.headline.approvalsWaiting.zero": "{count} Korrekturen offen · nichts überfällig",
  "overview.headline.grantsWithoutExpiry": "{count} Rechte ohne Enddatum · {second} Zugangsanfragen",
  "overview.headline.incidentsOpen": "{count} Vorfall offen · {second} Rückmeldungen fehlgeschlagen",
  "overview.headline.startingAndPayroll": "{count} Neuzugänge · Lohnabrechnung offen",
  "overview.watch.demandOutrunningSupply":
    "Die Aufträge steigen um {first} % und der Anteil mit Angebot fällt um {second} Punkte. Die Nachfrage kommt schneller, als das Angebot antworten kann.",
  "overview.watch.nobodyAnswered": "{first} Aufträge wurden eingestellt, auf die niemand geboten hat.",
  "roles.new.department": "Abteilung",
  "roles.new.pickDepartment": "Abteilung wählen…",
  "roles.new.departmentNeeded":
    "Wählen Sie die Abteilung der Rolle: ein QA-Tester gehört zu Produkt und Technik, ein Buchhalter zu Finanzen.",
  "screen.experiments": "Experimente",
  "screen.leads": "Interessenten",
  "screen.campaigns": "Kampagnen",
  "screen.referrals": "Empfehlungen",
  "experiments.title": "Experimente",
  "experiments.sub": "Eine Funktion für einen Teil der Kunden einschalten und vergleichen.",
  "experiments.loadFailed": "Experimente konnten nicht geladen werden.",
  "experiments.none": "Kein Experiment.",
  "experiments.start": "Experiment starten",
  "experiments.created": "{reference} angelegt",
  "experiments.moved": "{reference} bei {percent}%",
  "experiments.ship": "Ausrollen",
  "experiments.stop": "Stoppen",
  "experiments.sticky": "Eine Person bleibt in ihrer Gruppe: die Gruppe wird berechnet, nie gelost.",
  "experiments.col.name": "Experiment",
  "experiments.col.feature": "Funktion",
  "experiments.col.share": "Anteil",
  "experiments.col.status": "Status",
  "experiments.col.ramp": "Ausweiten",
  "experiments.status.draft": "Entwurf",
  "experiments.status.running": "Läuft",
  "experiments.status.stopped": "Gestoppt",
  "experiments.status.shipped": "Ausgerollt",
  "experiments.startTitle": "Experiment starten",
  "experiments.startBody": "Es beginnt bei 0%: anlegen und öffnen sind zwei Entscheidungen.",
  "experiments.startSubmit": "Anlegen",
  "experiments.featureNeeded": "Wählen Sie eine Kundenfunktion.",
  "experiments.pickFeature": "Wählen…",
  "experiments.field.name": "Was versucht wird",
  "experiments.field.feature": "Kundenfunktion",
  "experiments.field.featureHint": "Ein Experiment läuft auf Kunden, nie auf Mitarbeitenden.",
  "experiments.field.metric": "Was gemessen wird",
  "experiments.field.metricHint": "Von einem Menschen gelesen, nicht vom Code.",
  "leads.title": "Interessenten",
  "leads.sub": "Anbieter, die wir wollen und noch nicht haben.",
  "leads.loadFailed": "Interessenten konnten nicht geladen werden.",
  "leads.none": "Kein Interessent.",
  "leads.add": "Interessenten anlegen",
  "leads.added": "{name} angelegt",
  "leads.moved": "{name} aktualisiert",
  "leads.markContacted": "Kontaktiert",
  "leads.markLost": "Verloren",
  "leads.nameNeeded": "Geben Sie den Namen an.",
  "leads.status.new": "Neu",
  "leads.status.contacted": "Kontaktiert",
  "leads.status.signed_up": "Registriert",
  "leads.status.lost": "Verloren",
  "leads.source.coverage_gap": "Versorgungslücke",
  "leads.source.referral": "Empfehlung",
  "leads.source.inbound": "Eingehend",
  "leads.source.other": "Andere",
  "leads.addTitle": "Interessenten anlegen",
  "leads.addBody": "Jemand, der noch nicht auf der Plattform ist.",
  "leads.addSubmit": "Anlegen",
  "leads.field.name": "Name",
  "leads.field.phone": "Telefon",
  "leads.field.trade": "Gewerk",
  "leads.field.city": "Stadt",
  "leads.field.source": "Herkunft",
  "campaigns.title": "Kampagnen",
  "campaigns.sub": "Nachrichten an Anbieter und Kunden.",
  "campaigns.loadFailed": "Kampagnen konnten nicht geladen werden.",
  "campaigns.none": "Keine Kampagne.",
  "campaigns.draft": "Kampagne entwerfen",
  "campaigns.drafted": "{reference} bereit: {count} Empfänger",
  "campaigns.send": "Senden",
  "campaigns.sent": "An {count} Personen gesendet",
  "campaigns.wentTo": "Ging an {count}",
  "campaigns.budgetNeeded": "Eine SMS-Kampagne braucht ein Budget.",
  "campaigns.onlyInactive": "Nur die, die nichts getan haben",
  "campaigns.inAppFree": "Kostenlos: die Mitteilung kommt in der App an.",
  "campaigns.smsCosts": "Kostenpflichtig: jede SMS kostet, und Daten werden hier stundenweise bezahlt.",
  "campaigns.smsLength": "{count} Zeichen (über 160 sind zwei SMS pro Person)",
  "campaigns.channel.in_app": "In der App",
  "campaigns.channel.sms": "SMS",
  "campaigns.audience.pros": "Anbieter",
  "campaigns.audience.customers": "Kunden",
  "campaigns.draftTitle": "Kampagne entwerfen",
  "campaigns.draftBody": "Die Empfängerzahl wird beim Senden erneut ermittelt.",
  "campaigns.draftSubmit": "Entwerfen",
  "campaigns.field.name": "Name",
  "campaigns.field.channel": "Kanal",
  "campaigns.field.audience": "Empfänger",
  "campaigns.field.body": "Nachricht",
  "campaigns.field.bodyHint": "Auf Französisch, für alle sichtbar.",
  "campaigns.field.budget": "Budget (GNF)",
  "campaigns.field.budgetHint": "Was ausgegeben werden darf.",
  "referrals.title": "Empfehlungen",
  "referrals.sub": "Anbieter, die andere Anbieter gebracht haben.",
  "referrals.loadFailed": "Empfehlungen konnten nicht geladen werden.",
  "referrals.none": "Keine Empfehlung.",
  "referrals.unused": "Code unbenutzt",
  "referrals.paidOut": "{amount} {currency} ausgezahlt",
  "referrals.paidWhen": "Wird gezahlt, wenn der geworbene Anbieter seinen ersten Auftrag gewinnt.",
  "referrals.status.open": "Offen",
  "referrals.status.signed_up": "Registriert",
  "referrals.status.rewarded": "Ausgezahlt",
  "referrals.status.expired": "Abgelaufen",
  "screen.incidents": "Störungen",
  "incidents.title": "Störungen",
  "incidents.sub": "Was kaputt ist und was den Leuten gesagt wird.",
  "incidents.loadFailed": "Störungen konnten nicht geladen werden.",
  "incidents.openOne": "Störung eröffnen",
  "incidents.happeningNow": "Läuft gerade",
  "incidents.allWell": "Alles funktioniert.",
  "incidents.over": "Beendet",
  "incidents.open": "Öffnen",
  "incidents.notAnnounced": "Nicht angekündigt",
  "incidents.started": "Begonnen am {when}",
  "incidents.opened": "{reference} eröffnet",
  "incidents.updated": "{reference} aktualisiert",
  "incidents.status.open": "Offen",
  "incidents.status.monitoring": "Unter Beobachtung",
  "incidents.status.resolved": "Behoben",
  "incidents.severity.degraded": "Eingeschränkt",
  "incidents.severity.down": "Ausgefallen",
  "incidents.openTitle": "Störung eröffnen",
  "incidents.openBody":
    "Solange sie offen ist, sehen alle Kundinnen und Kunden ein Banner. Leer lassen, um die Störung zu verfolgen, ohne etwas anzukündigen.",
  "incidents.openSubmit": "Eröffnen",
  "incidents.titleNeeded": "Sagen Sie in wenigen Worten, was kaputt ist.",
  "incidents.causeNeeded": "Sagen Sie vor dem Schließen, warum es passiert ist.",
  "incidents.updateSubmit": "Speichern",
  "incidents.field.title": "Was kaputt ist",
  "incidents.field.severity": "Schwere",
  "incidents.field.banner": "Nachricht an Kunden",
  "incidents.field.bannerHint": "Auf Französisch, für alle sichtbar. Leer: niemand wird informiert.",
  "incidents.field.status": "Status",
  "incidents.field.note": "Notiz",
  "incidents.field.noteHint": "Kommt in den Verlauf, den nichts überschreiben kann.",
  "incidents.field.cause": "Ursache",
  "screen.overview": "Übersicht",
  "overview.sub": "Was heute auf Sie wartet.",
  "overview.loadFailed": "Die Übersicht konnte nicht geladen werden.",
  "overview.needsYou": "Was Sie braucht",
  "overview.nothingToShow": "Nichts zu zeigen: Sie halten nichts von dem, was diese Abteilung liest.",
  "overview.waitingDays": "{days} T. Wartezeit",
  "overview.figure.leaveToDecide": "Urlaub zu entscheiden",
  "overview.figure.reviewsOwed": "Beurteilungen zu schreiben",
  "overview.figure.openRoles": "Offene Stellen",
  "overview.figure.startingSoon": "Diese Woche Anfang",
  "overview.figure.payrollOpen": "Offene Lohnläufe",
  "overview.figure.leaversStillHolding": "Ausgeschiedene mit Zugang",
  "overview.figure.accessRequests": "Zugangsanfragen",
  "overview.figure.grantsWithoutExpiry": "Rechte ohne Enddatum",
  "overview.figure.rolesHeldByNobody": "Rollen ohne Inhaber",
  "overview.figure.staffWithSignIn": "Aktive Mitarbeitende",
  "overview.figure.casesBreaching": "Fälle kurz vor Frist",
  "overview.figure.casesMine": "Ihre Fälle",
  "overview.figure.casesUnclaimed": "Fälle ohne Inhaber",
  "overview.figure.casesOpen": "Offene Fälle",
  "overview.figure.criticalUnreferred": "Kritische Meldungen nicht übergeben",
  "overview.figure.verificationsWaiting": "Prüfungen offen",
  "overview.figure.verificationsOverADay": "Länger als 24 Std. offen",
  "overview.figure.reportsOpen": "Offene Meldungen",
  "overview.figure.restrictionsToReview": "Sperren zu prüfen",
  "overview.figure.legalHolds": "Rechtliche Sperren",
  "overview.figure.approvalsWaiting": "Korrekturen zu genehmigen",
  "overview.figure.feesThisMonth": "Zahlungen diesen Monat",
  "overview.figure.creditsOutstanding": "Offene Guthaben",
  "overview.figure.subscriptionsEndingSoon": "Abos enden in 7 T.",
  "overview.figure.providerEventsFailed": "Fehlgeschlagene Rückmeldungen",
  "overview.figure.rulesScheduled": "Geplante Regeln",
  "overview.figure.featureExceptions": "Funktionsausnahmen",
  "overview.figure.prosSignedUp": "Anbieter registriert (30 T.)",
  "overview.figure.prosApproved": "Anbieter freigegeben (30 T.)",
  "overview.figure.prosNeverQuoted": "Anbieter ohne Angebot",
  "overview.figure.prosWithWork": "Anbieter mit Auftrag",
  "overview.figure.promosRunning": "Laufende Aktionen",
  "overview.figure.jobsThisMonth": "Aufträge diesen Monat",
  "overview.figure.customers": "Kundenkonten",
  "overview.item.caseDueOwned": "Ihr Fall läuft ab",
  "overview.item.caseDueUnclaimed": "Herrenloser Fall läuft ab",
  "screen.marketplaceOverview": "Übersicht",
  "screen.jobs": "Aufträge",
  "screen.providers": "Anbieter",
  "screen.categories": "Gewerke",
  "screen.marketplaceReviews": "Bewertungen",
  "marketplace.title": "Der Markt",
  "marketplace.sub": "Was gefragt wurde, wer es kann, und wo niemand es kann.",
  "marketplace.loadFailed": "Der Markt konnte nicht geladen werden.",
  "marketplace.posted": "Aufträge eingestellt",
  "marketplace.gotQuote": "Mit Angebot",
  "marketplace.accepted": "Angenommen",
  "marketplace.noBids": "Niemand hat geboten",
  "marketplace.toFirstQuote": "Bis zum 1. Angebot",
  "marketplace.hoursShort": "{hours} Std.",
  "marketplace.nobodyBid": "Darauf hat niemand geboten",
  "marketplace.nobodyBidHint": "Älteste zuerst.",
  "marketplace.allAnswered": "Auf alles Eingestellte gab es eine Antwort.",
  "marketplace.waitingDays": "{days} T. Wartezeit",
  "marketplace.noProHere": "hier kann das niemand",
  "marketplace.prosNearby": "{count} Anbieter in der Nähe",
  "marketplace.demandNoSupply": "Nachfrage ohne Angebot",
  "marketplace.demandNoSupplyHint": "In den letzten 30 Tagen gefragt, ohne Anbieter vor Ort.",
  "marketplace.everywhereCovered": "Jede Stadt hat Anbieter für ihre Nachfrage.",
  "marketplace.activePros": "{count} Anbieter können heute arbeiten",
  "marketplace.jobs": "Aufträge",
  "marketplace.jobsSub": "Jeder Auftrag und wie weit er kam.",
  "marketplace.noJobs": "Kein Auftrag passt.",
  "marketplace.filter.all": "Alle",
  "marketplace.filter.no_quotes": "Ohne Angebot",
  "marketplace.filter.requested": "Eingestellt",
  "marketplace.filter.quoted": "Mit Angebot",
  "marketplace.filter.accepted": "Angenommen",
  "marketplace.filter.cancelled": "Storniert",
  "marketplace.status.requested": "Eingestellt",
  "marketplace.status.quoted": "Mit Angebot",
  "marketplace.status.accepted": "Angenommen",
  "marketplace.status.cancelled": "Storniert",
  "marketplace.status.declined": "Abgelehnt",
  "marketplace.status.expired": "Abgelaufen",
  "marketplace.col.job": "Auftrag",
  "marketplace.col.city": "Stadt",
  "marketplace.col.posted": "Eingestellt",
  "marketplace.col.quotes": "Angebote",
  "marketplace.col.estimate": "Schätzung",
  "marketplace.col.status": "Status",
  "marketplace.providers": "Anbieter",
  "marketplace.providersSub": "Wer arbeiten kann und wer noch nicht arbeitet.",
  "marketplace.noPros": "Kein Anbieter passt.",
  "marketplace.neverQuoted": "Nie geboten",
  "marketplace.allPros": "Alle",
  "marketplace.unverified": "Nicht geprüft",
  "marketplace.col.pro": "Anbieter",
  "marketplace.col.skills": "Gewerke",
  "marketplace.col.quotesGiven": "Abgegebene Angebote",
  "marketplace.col.rating": "Bewertung",
  "marketplace.categories": "Gewerke",
  "marketplace.categoriesSub": "Was gefragt wird und ob es jemand kann.",
  "marketplace.noCategories": "Kein Gewerk vorhanden.",
  "marketplace.noDemand": "Keine Nachfrage",
  "marketplace.coverageHint": "Abdeckung ist der Anteil der Aufträge mit mindestens einem Angebot.",
  "marketplace.col.category": "Gewerk",
  "marketplace.col.jobs": "Aufträge",
  "marketplace.col.pros": "Anbieter",
  "marketplace.col.coverage": "Abdeckung",
  "marketplace.col.uncovered": "Städte ohne Anbieter",
  "marketplace.reviews": "Bewertungen",
  "marketplace.reviewsSub": "Was Kunden über Anbieter sagen.",
  "marketplace.noReviews": "Keine Bewertung passt.",
  "marketplace.poorOnly": "3 Sterne oder weniger",
  "marketplace.allReviews": "Alle",
  "marketplace.averageIs": "Durchschnitt {average} bei {total} Bewertungen",
  "nav.group.everyone": "Alle",
  "workspace.yours": "Ihre Bereiche",
  "department.executive": "Geschäftsführung",
  "department.executive.blurb": "Das Unternehmen auf einen Blick",
  "department.marketplace": "Marktbetrieb",
  "department.marketplace.blurb": "Aufträge, Anbieter, Kategorien",
  "department.support": "Kundenservice",
  "department.support.blurb": "Fälle, Kunden und Profis",
  "department.trust": "Vertrauen und Sicherheit",
  "department.trust.blurb": "Prüfungen, Meldungen, Sperren",
  "department.finance": "Finanzen",
  "department.finance.blurb": "Gebühren, Korrekturen, Abonnements",
  "department.growth": "Vertrieb und Wachstum",
  "department.growth.blurb": "Profis gewinnen, Aktionen",
  "department.product": "Produkt und Technik",
  "department.product.blurb": "Störungen, Regeln, Rollouts",
  "department.people": "Personalwesen",
  "department.people.blurb": "Einstellung, Beschäftigung, Lohn",
  "department.administration": "Verwaltung und Sicherheit",
  "department.administration.blurb": "Rollen, Berechtigungen, Protokoll",
  "department.legal": "Recht und Compliance",
  "department.legal.blurb": "Bedingungen, Daten, Rechtsanfragen",
  "nav.signedInAs": "Angemeldet als",
  "nav.screenCount": "{roles} · {visible} von {total} Seiten",
  "profile.title": "Dein Profil",
  "profile.open": "Profil öffnen",
  "profile.identity": "Du",
  "profile.email": "E-Mail",
  "profile.roles": "Rollen",
  "profile.permits": "Was deine Rollen erlauben",
  "profile.screensOpen": "{visible} von {total} Bereichen offen",
  "profile.actionCount": "{count} Aktionen möglich",
  "profile.noPermissions": "Keine Berechtigung mit Grenze: nichts braucht eine Freigabe.",
  "profile.preferences": "Einstellungen",
  "profile.emailLabel": "E-Mail:",
  "profile.uuidLabel": "UUID:",
  "profile.rolesLabel": "Rollen",
  "profile.figure.screens": "Bildschirme, die Ihnen offenstehen",
  "profile.figure.actions": "Aktionen, die Sie ausführen können",
  "profile.figure.ceilings": "mit einer Obergrenze",
  "profile.ceilingHeading": "Das Höchste, was Sie allein tun dürfen",
  "profile.ceilingHint":
    "Über einer Obergrenze öffnet sich die Aktion trotzdem — Ihr Versuch wird zu einer Freigabeanfrage.",
  "profile.signOutHint": "Du musst dich neu anmelden.",
  "header.signOut": "Abmelden",
  "header.signingOut": "Abmeldung…",
  "header.language": "Sprache der Konsole",
  "header.theme": "Design",
  "header.theme.system": "System",
  "header.theme.light": "Hell",
  "header.theme.dark": "Dunkel",
  "header.languageNotSaved": "Nur für diese Sitzung: Diese Wahl ist noch nicht in Ihrem Konto gespeichert.",
  "shell.loading": "Konsole wird geladen…",
  "shell.loadFailed": "Ihr Profil konnte nicht geladen werden",
  "shell.retry": "Erneut versuchen",
  "shell.refreshFailed":
    "Ihre Berechtigungen konnten nicht aktualisiert werden. Die Konsole zeigt die um {time} geladenen an.",
  "shell.noScreens":
    "Für Ihre Rollen ({roles}) ist keine Seite freigegeben. Bitten Sie eine verantwortliche Person um Zugriff.",
  "shell.noRoles": "Ihnen ist noch kein Bildschirm zugänglich. Bitten Sie eine Führungskraft um eine Rolle.",
  "connection.retrying": "Instabile Verbindung · neuer Versuch {attempt} von {max}…",

  // ── Screens ──
  "screen.dashboard": "Übersicht",
  "screen.myRequests": "Meine Anfragen",
  "screen.myTimeOff": "Meine Abwesenheiten",
  "myTimeOff.sub": "Ihr Guthaben, was Sie beantragt haben, und wer sonst weg ist.",
  "myRequests.sub": "Worum Sie gebeten haben und wie es um jede Antwort steht.",
  "screen.as.directory": "Verzeichnis",
  "screen.as.market": "Markt",
  "screen.as.employees": "Beschäftigte",
  "screen.as.recruiting": "Personalsuche",
  "screen.as.learning": "Weiterbildung",
  "screen.as.payroll": "Lohnabrechnung",
  "screen.as.money": "Geld",
  "screen.as.workforce": "Belegschaft",
  "screen.as.accounts": "Konten",
  "screen.cases": "Fälle",
  "screen.customers": "Kunden & Profis",
  "screen.verifications": "Prüfungen",
  "screen.reports": "Meldungen",
  "screen.billing": "Preise und Abos",
  "screen.payments": "Zahlungen",
  "screen.features": "Funktionsfreischaltungen",
  "screen.userSettings": "Benutzereinstellungen",
  "settings.title": "Benutzereinstellungen",
  "settings.tab.app": "App-Nutzer",
  "settings.tab.employee": "Mitarbeiter",
  "settings.col.setting": "Einstellungen",
  "settings.default": "Standardwert",
  "settings.setHere": "Für diese Person gesetzt",
  "settings.clear": "Löschen",
  "settings.none": "Keine Einstellung passt.",
  "screen.actions": "Aktionen",
  "actions.title": "Aktionen",
  "actions.col.action": "Aktion",
  "actions.effect.read": "Lesen",
  "actions.effect.write": "Schreiben",
  "actions.alsoGates": "+{count} weitere",
  "actions.col.area": "Bereich",
  "actions.col.effect": "Wirkung",
  "actions.col.audited": "Auditiert",
  "actions.col.needs": "Erfordert",
  "actions.needs.nothing": "Nichts",
  "actions.yes": "Ja",
  "actions.no": "Nein",
  "actions.surface.system": "System",
  "actions.search": "Aktion suchen",
  "actions.none": "Keine Aktion passt.",
  "screen.permissions": "Berechtigungen",
  "permissions.title": "Berechtigungen",
  "permissions.tab.role": "Rolle",
  "permissions.tab.employee": "Mitarbeiter",
  "permissions.col.limit": "Grenze",
  "permissions.col.expires": "Läuft ab",
  "permissions.source.staffRole": "Per Rolle",
  "permissions.source.staffMember": "Ausnahme",
  "permissions.source.allStaff": "Alle Mitarbeiter",
  "permissions.source.default": "Keine",
  "permissions.noLimit": "Keine Obergrenze",
  "permissions.featureOff": "Funktion ist aus",
  "permissions.approvable": "Kann anfragen",
  "permissions.pickRole": "Rolle wählen",
  "audit.kind.featureEnablements": "Funktionsfreischaltungen",
  "audit.kind.permissions": "Berechtigungen",
  "audit.kind.teams": "Rollen und Teams",
  "audit.kind.actions": "Aktionen",
  "audit.kind.userSettings": "Benutzereinstellungen",
  "screen.roles": "Rollen",
  "roles.title": "Rollen",
  "roles.grants.none": "Gewährt noch nichts",
  "roles.grants.permissions": "Berechtigungen ändern",
  "roles.grants.features": "Funktionen ändern",
  "roles.explain":
    "Was jemand gelernt hat und tun darf. Eine Rolle ist in jedem Team dieselbe — Leiten ist deshalb keine, das wird unter Teams festgelegt.",
  "roles.col.name": "Rolle",
  "roles.col.description": "Was sie umfasst",
  "roles.col.members": "Personen",
  "roles.none": "Noch keine Rollen.",
  "roles.loading": "Rollen werden geladen…",
  "roles.loadFailed": "Die Rollen konnten nicht gelesen werden.",
  "roles.retry": "Erneut versuchen",
  "roles.edit": "Umbenennen",
  "roles.save": "Speichern",
  "roles.cancel": "Abbrechen",
  "roles.created": "Rolle erstellt.",
  "roles.new": "Neue Rolle",
  "roles.new.title": "Neue Rolle",
  "roles.back": "← Alle Rollen",
  "roles.opens": "Was sie öffnet",
  "roles.opens.explain":
    "Die Bildschirme, die diese Rolle erreicht, aus den Häkchen links. Keine eigene Liste — ein Bildschirm erscheint, weil die Rolle das Nötige hält, und die API prüft dasselbe.",
  "roles.new.name": "Name",
  "roles.new.description": "Was sie umfasst",
  "roles.new.create": "Rolle erstellen",
  "roles.new.nameNeeded": "Geben Sie der Rolle einen Namen.",
  "roles.new.grantsNeeded": "Wählen Sie, wofür die Rolle da ist: Eine Rolle, die nichts gewährt, hat keine Wirkung.",
  "roles.new.grants": "Was sie nutzen kann",
  "roles.new.permissions": "Was sie tun darf",
  "roles.lock.critical": "Nur ein Owner kann das gewähren.",
  "roles.lock.high": "Sie können nur gewähren, was Sie selbst haben.",
  "roles.lock.readOnly": "Sie können auf einer Rolle nichts gewähren.",
  "roles.lock.featureFirst": "Schalten Sie zuerst das darunter frei.",
  "screen.teams": "Teams",
  "teams.title": "Teams",
  "teams.col.name": "Team",
  "teams.col.purpose": "Wofür es da ist",
  "teams.col.lead": "Geleitet von",
  "teams.col.members": "Personen",
  "teams.col.status": "Status",
  "teams.col.person": "Person",
  "teams.col.roles": "Rolle",
  "teams.col.onTeam": "Im Team",
  "teams.col.leads": "Leitet es",
  "teams.open": "Öffnen",
  "teams.back": "Alle Teams",
  "teams.none": "Noch keine Teams.",
  "teams.noMembers": "Noch niemand in diesem Team.",
  "teams.noOthers": "Alle sind bereits in diesem Team.",
  "teams.loading": "Teams werden geladen…",
  "teams.loadFailed": "Die Teams konnten nicht gelesen werden.",
  "teams.retry": "Erneut versuchen",
  "teams.status.open": "Offen",
  "teams.status.closed": "Geschlossen",
  "teams.rename": "Umbenennen",
  "teams.close": "Team schließen",
  "teams.reopen": "Wieder öffnen",
  "teams.closedReason": "Geschlossen",
  "teams.reopenedReason": "Wieder geöffnet",
  "teams.closedNote": "Geschlossen. Alle bleiben darin: beim Wiederöffnen sind sie noch da.",
  "teams.new": "Neues Team",
  "teams.explain":
    "Eine Gruppe von Menschen, die dieselbe Arbeit tun, mit einer Leitung. Die Arbeit geht an das Team, nicht an eine Person: Wer heute dran ist, nimmt die nächste. Ein Team gewährt nichts.",
  "teams.new.people":
    "Wer von Anfang an dabei ist. Setzen Sie ein Häkchen bei „Leitet es“, um zu sagen, wessen Leute das sind: Sie verteilen die Arbeit des Teams neu und springen für jeden ein.",
  "teams.new.name": "Name",
  "teams.new.purpose": "Wofür es da ist",
  "teams.new.create": "Team erstellen",
  "teams.new.cancel": "Abbrechen",
  "teams.new.nameNeeded": "Geben Sie dem Team einen Namen.",
  "teams.nameTaken": "Ein Team mit diesem Namen gibt es bereits.",
  "teams.created": "Team erstellt.",
  "teams.leadHint": "Das sind ihre Leute: Sie verteilen die Arbeit des Teams neu und springen für jeden ein.",
  "teams.leadNeedsMember": "Leiten kann nur, wer im Team ist.",
  "teams.noLead": "Keine Leitung",
  "teams.gone": "Wer ausgeschieden ist, kann keinem Team hinzugefügt werden.",
  "teams.locked": "Sie können Teams nicht ändern.",
  "screen.rules": "Regeln",
  "screen.hr": "Personalwesen",
  "sub.hr": "Jeden nachschlagen. Einstellung, Akten und alles darunter gehören dem Personalwesen.",
  "screen.team": "Rollen & Team",
  "screen.audit": "Walliyo-Audits",

  // ── Locked screen (opened by URL without the feature) ──
  "lock.requires": "Diese Seite erfordert",
  "lock.or": " oder ",
  "lock.roles": "Ihre Rollen ({roles}) enthalten sie nicht, daher erscheint sie nicht in Ihrem Menü.",
  "lock.noRoles": "Sie haben keine Rolle, daher erscheint sie nicht in Ihrem Menü.",

  // ── Sign-in ──
  "signIn.title": "Anmeldung bei der Konsole",
  "signIn.subtitle": "Nur für das Walliyo-Team. Sie erhalten einen Code per SMS.",
  "signIn.email": "Geschäftliche E-Mail",
  "signIn.password": "Passwort",
  "signIn.submit": "Weiter",
  "signIn.submitting": "Wird geprüft…",
  "verify.title": "Bestätigungscode",
  "verify.subtitle": "Wir haben einen Code per SMS an {phone} gesendet.",
  "verify.code": "Per SMS erhaltener Code",
  "verify.submit": "Anmelden",
  "verify.submitting": "Wird geprüft…",
  "verify.expiresIn": "Der Code läuft in {time} ab.",
  "verify.expired": "Der Code ist abgelaufen. Melden Sie sich erneut an, um einen neuen zu erhalten.",
  "verify.restart": "Anmeldung neu starten",

  // ── Errors: what failed and what to do (ADR-0016 §2.5) ──
  "error.requestId": "Anfrage-ID: {id}",
  "error.noRequestId": "Keine Anfrage-ID: Der Server hat nicht geantwortet.",
  "error.signIn.unauthenticated": "E-Mail oder Passwort falsch. Prüfen Sie beides und versuchen Sie es erneut.",
  "error.signIn.validation_error": "Geben Sie eine gültige E-Mail und ein gültiges Passwort ein.",
  "error.verify.validation_error": "Geben Sie den 6-stelligen Code aus der SMS ein.",
  "error.verify.session_expired": "Der Code ist abgelaufen. Melden Sie sich erneut an, um einen neuen zu erhalten.",
  "error.verify.unauthenticated": "Diese Code-Anfrage ist nicht mehr gültig. Melden Sie sich erneut an.",
  "error.unauthenticated": "Sie sind nicht mehr angemeldet. Melden Sie sich erneut an.",
  "error.session_expired": "Ihre Sitzung ist abgelaufen. Melden Sie sich erneut an.",
  "error.feature_not_enabled":
    "Ihre Rollen erlauben diese Aktion nicht. Bitten Sie eine verantwortliche Person um Zugriff.",
  "error.invalid_code": "Falscher Code. Prüfen Sie die SMS und geben Sie den Code erneut ein.",
  "error.too_many_attempts": "Zu viele Versuche. Warten Sie einige Minuten und melden Sie sich dann erneut an.",
  "error.stale_version":
    "Dies wurde geändert, während Sie es angesehen haben. Laden Sie neu, um die aktuelle Version zu sehen.",
  "error.validation_error": "Einige Angaben sind ungültig. Korrigieren Sie sie und versuchen Sie es erneut.",
  "error.internal_error":
    "Auf dem Server ist ein Fehler aufgetreten. Versuchen Sie es erneut; falls es weiter passiert, geben Sie die untenstehende ID an das Technikteam weiter.",
  "error.network_error":
    "Keine Antwort vom Server nach mehreren Versuchen. Prüfen Sie Ihre Verbindung und versuchen Sie es erneut.",
  "error.network_error.write":
    "Keine Antwort vom Server: Die Anfrage ist möglicherweise durchgegangen. Versuchen Sie es erneut, sie wird nicht doppelt ausgeführt.",
  "error.unexpected_response":
    "Unerwartete Antwort vom Server. Versuchen Sie es erneut; falls es weiter passiert, geben Sie die untenstehende ID an das Technikteam weiter.",
  "error.unknown":
    "In der Konsole ist ein unerwarteter Fehler aufgetreten. Laden Sie die Seite neu; falls es weiter passiert, informieren Sie das Technikteam.",

  "sub.cases": "Gemeinsame Warteschlange · Anrufe, E-Mails und Chat an einem Ort",

  "sub.customers": "Einen Kunden oder Profi suchen und sein ganzes Konto sehen",

  "sub.verifications": "Die Identität der Profis prüfen, bevor man sie finden kann",

  "sub.reports": "Meldungen zu Profis oder Kunden, nach Schweregrad",

  "sub.billing": "Preise je Land, Aktionen und Rabatte · ein Profi behält den Preis seines Abschlusses",
  "sub.payments": "Gebühren der Profis · Orange Money und Karte",

  "sub.features": "Ein- oder ausschalten pro Ebene: alle, eine Region, eine Version, eine Person",

  "sub.rules": "Werte des Unternehmens · jede Änderung erzeugt eine Version",

  // ── Fonctionnalités ──
  "features.title": "Funktionsfreischaltungen",
  "features.loading": "Funktionen werden geladen…",
  "features.loadFailed": "Die Funktionen konnten nicht geladen werden.",
  "features.retry": "Erneut versuchen",
  "features.close": "Schließen",
  "features.pick": "Wählen Sie eine Funktion, um ihre Ausnahmen zu sehen.",
  "features.notFound": "Diese Funktion gibt es nicht (mehr). Wählen Sie eine aus der Liste.",
  "features.subject.for": "Datensatz",
  "features.subject.allClients": "Alle Kunden",
  "features.subject.allPros": "Alle Profis",
  "features.subject.user": "Benutzer",
  "features.subject.uuid": "UUID",
  "features.subject.uuidHint": "31290388",
  "features.subject.unknown": "UUID nicht gefunden.",
  "features.kind.client": "Kunde",
  "features.kind.pro": "Profi",
  "features.col.source": "Quelle",
  "features.col.value": "Wert",
  "features.col.category": "Kategorie",
  "features.value.on": "An",
  "features.value.off": "Aus",
  "features.source.default": "Standard",
  "features.source.thisUser": "Dieser Benutzer",
  "features.source.user": "Ein Benutzer",
  "features.source.allClients": "Alle Kunden",
  "features.source.allPros": "Alle Profis",
  "features.source.proProfile": "Profi-Profil",
  "features.source.region": "Region",
  "features.source.appVersion": "App-Version",
  "features.source.system": "System",
  "features.locked.owner": "Nur ein Owner kann das ändern",
  "features.locked.scope": "Auf dieser Ebene nicht setzbar",
  "features.save.discard": "Verwerfen",
  "features.save.sending": "Wird gespeichert…",
  "features.save.nothing": "Nichts zu speichern: keine Änderung.",
  "features.save.locked": "Eine der Änderungen steht Ihnen nicht zu.",
  "features.save.reasonNeeded": "Sagen Sie kurz, warum.",
  "features.save.leave": "Sie haben nicht gespeicherte Änderungen.",
  "features.clear": "Zum geerbten Wert zurückkehren",
  "features.cleared": "Wird gelöscht",
  "features.filter.any": "Alle",
  "features.filter.own": "Hier gesetzt",
  "features.filter.inherited": "Geerbt",
  "features.filter.search": "Suchen",
  "features.filter.none": "Keine Funktion passt zu diesen Filtern.",
  "features.surface.app": "Walliyo-App",
  "features.surface.console": "Walliyo-Konsole",
  "features.save.title.one": "1 nicht gespeicherte Änderung",
  "features.save.title.many": "{count} nicht gespeicherte Änderungen",
  "features.save.submit.one": "1 Änderung speichern",
  "features.save.submit.many": "{count} Änderungen speichern",
  "features.save.done.one": "1 Änderung gespeichert.",
  "features.save.done.many": "{count} Änderungen gespeichert.",
  "features.unsaved": "ungespeichert",
  "features.surface.consoleSoon":
    "Die {count} Konsolenfunktionen lassen sich bisher nur pro Rolle setzen. Pro Person folgt hier.",
  "features.surface.consoleWhere": "Rollen & Team öffnen",
  "features.subject.isStaff":
    "Diese Nummer gehört einem Walliyo-Mitarbeiter. Seine Funktionen stehen im Tab Walliyo-Konsole.",
  "features.subject.search": "Suchen",
  "features.subject.searching": "Suche…",
  "features.subject.needsUuid": "Geben Sie die acht Ziffern der UUID ein.",
  "features.subject.pickFirst": "Wählen Sie, für wen, und suchen Sie dann.",
  "features.subject.stale":
    "Diese Ergebnisse stammen aus Ihrer letzten Suche. Suchen Sie erneut, um sie zu aktualisieren.",
  "features.pill.allClients": "Alle Kundendatensätze",
  "features.pill.allPros": "Alle Profi-Datensätze",
  "features.pill.unknown": "Unbekannter Benutzer",
  "features.pill.staff": "Walliyo-Mitarbeiter",
  "features.subject.allStaff": "Alle Mitarbeiter",
  "features.subject.staffMember": "Mitarbeiter",
  "features.staffSource.override": "Ausnahme",
  "features.staffSource.byRole": "Per Rolle",
  "features.staffSource.byRoleNamed": "Per Rolle: {roles}",
  "features.staffSource.allStaff": "Alle Mitarbeiter",
  "features.locked.ownAccount": "Sie können Ihr eigenes Konto nicht ändern",
  "features.locked.grant": "Diese dürfen Sie nicht vergeben",
  "features.pill.allStaff": "Alle Mitarbeiter-Datensätze",
  "features.pill.unknownStaff": "Unbekannter Mitarbeiter",
  "features.subject.isCustomer":
    "Diese Nummer gehört einem Kunden, keinem Mitarbeiter. Seine Funktionen stehen im Tab Walliyo-App.",
  "features.subject.employeeUuid": "Mitarbeiter-UUID",
  "features.subject.staffUuidHint": "27217102",
  "features.col.feature": "Funktion",
  "features.col.default": "Standard",
  "features.col.exceptions": "Ausnahmen",
  "features.for.clients": "Für Kunden",
  "features.for.pros": "Für Profis",
  "features.for.everyone": "Für alle",
  "features.on": "An",
  "features.off": "Aus",
  "features.planned": "Geplant",
  "features.planned.note": "Geplant: Ein Wert kann gesetzt werden, aber noch liest ihn kein Code.",
  "features.detail.default": "Standard: {value}",
  "features.sensitivity.open": "Sensibilität {level}: Jede Person, die App-Funktionen verwaltet, kann sie ändern.",
  "features.sensitivity.critical":
    "Kritische Sensibilität: Nur ein Inhaber kann sie ändern, und die Inhaber werden benachrichtigt.",
  "features.locked.critical":
    "Gesperrt: Diese Funktion ist kritisch, nur ein Inhaber kann sie ändern. Ihre Rollen ({roles}) machen Sie nicht zum Inhaber; der Server würde ebenfalls ablehnen.",
  "features.exceptions.title": "Ausnahmen nach Ebene",
  "features.col.scope": "Ebene",
  "features.col.target": "Ziel",
  "features.col.reason": "Begründung",
  "features.col.expires": "Läuft ab",
  "features.col.actions": "Verwalten",
  "features.default.scope": "Standard",
  "features.default.reason": "Standardwert",
  "features.exceptions.none": "Keine Ausnahmen: Der Standardwert gilt für alle.",
  "features.expired": "Abgelaufen",
  "features.expires.never": "Bis zur Änderung",
  "features.edit": "Bearbeiten",
  "features.remove": "Entfernen",
  "features.add": "Ausnahme hinzufügen",
  "features.scope.system": "System",
  "features.scope.allClients": "Alle Kunden",
  "features.scope.allPros": "Alle Profis",
  "features.scope.region": "Region",
  "features.scope.appVersion": "App-Version",
  "features.scope.user": "Eine Person",
  "features.scope.proProfile": "Ein Profi-Profil",
  "features.scope.clientProfile": "Ein Kundenprofil",
  "features.target.everyone": "Alle",
  "features.target.allClients": "Alle Kunden",
  "features.target.allPros": "Alle Profis",
  "features.target.appVersion": "Version {version} und neuer",
  "features.target.unnamed": "Konto ohne Namen",
  "features.form.addTitle": "Neue Ausnahme",
  "features.form.editTitle": "Ausnahme bearbeiten",
  "features.form.scope": "Ebene",
  "features.form.scopePick": "Ebene wählen…",
  "features.form.scopeUnsupported": "{scope} (noch nicht möglich)",
  "features.form.clientProfileNote":
    "Ein Kundenprofil kann noch keinen Wert erhalten: Kundenprofile gibt es noch nicht. Für einen Kunden wählen Sie „Eine Person“.",
  "features.form.needsSearch": "Die Wahl einer Person oder eines Profi-Profils erfordert",
  "features.form.needsView": "Das Profi-Profil eines Kontos zu finden erfordert",
  "features.form.region": "Region",
  "features.form.regionPick": "Region wählen…",
  "features.form.appVersion": "Ab Version",
  "features.form.appVersionHint": "Zum Beispiel 2.4.0: gilt für diese und neuere Versionen.",
  "features.form.person": "Person",
  "features.form.pro": "Profi-Profil",
  "features.form.search": "Suchen",
  "features.form.searching": "Suche…",
  "features.form.searchPlaceholder": "Name, Telefon, E-Mail oder Konto-ID",
  "features.form.noResults": "Kein Konto passt.",
  "features.form.pickedChange": "Ändern",
  "features.form.value": "Wert",
  "features.form.reason": "Begründung (Pflicht)",
  "features.form.reasonHint": "Wird mit der Änderung im Audit-Protokoll gespeichert.",
  "features.form.expires": "Läuft ab am",
  "features.form.expiresHint": "Optional. Leer: bis zur Änderung.",
  "features.form.version": "Basiert auf Version {version} dieser Ausnahme.",
  "features.form.submitAdd": "Ausnahme hinzufügen",
  "features.form.submitEdit": "Speichern",
  "features.form.cancel": "Abbrechen",
  "features.form.sending": "Wird gesendet…",
  "features.problem.scope": "Wählen Sie eine der angebotenen Ebenen.",
  "features.problem.region": "Wählen Sie eine Region.",
  "features.problem.appVersion": "Geben Sie eine Version wie 2.4.0 ein.",
  "features.problem.person": "Suchen Sie die Person und wählen Sie sie aus den Ergebnissen.",
  "features.problem.pro": "Suchen Sie den Profi und wählen Sie ein Konto mit Profi-Profil.",
  "features.problem.exists": "Für dieses Ziel gibt es bereits eine Ausnahme: Bearbeiten Sie diese.",
  "features.problem.reason": "Geben Sie eine kurze Begründung an.",
  "features.problem.expiryInvalid": "Dieses Ablaufdatum ist ungültig.",
  "features.problem.expiryPast": "Das Ablaufdatum muss in der Zukunft liegen, oder lassen Sie es leer.",
  "features.refused.critical": "Abgelehnt: Diese Funktion ist kritisch, nur ein Inhaber kann sie ändern.",
  "features.refused.scopeNotSupported":
    "Abgelehnt: Ein Kundenprofil kann noch keinen Wert erhalten, Kundenprofile gibt es noch nicht. Wählen Sie „Eine Person“.",
  "features.refused.scopeNotAllowed":
    "Abgelehnt: Diese Funktion kann auf dieser Ebene nicht gesetzt werden. Wählen Sie eine der angebotenen Ebenen.",
  "features.refused.expiryInPast":
    "Abgelehnt: Das Ablaufdatum liegt in der Vergangenheit. Wählen Sie ein späteres Datum oder lassen Sie es leer.",
  "features.refused.targetNotFound":
    "Abgelehnt: Dieses Konto oder Profi-Profil gibt es nicht mehr. Suchen Sie es erneut.",
  "features.stale":
    "Jemand hat diese Funktion inzwischen geändert. Laden Sie neu, um die aktuellen Ausnahmen zu sehen, und versuchen Sie es erneut.",
  "features.done.added": "Ausnahme hinzugefügt.",
  "features.done.saved": "Ausnahme gespeichert.",
  "features.done.removed": "Ausnahme entfernt: Die breitere Ebene oder der Standardwert gilt wieder.",
  "features.remove.title": "Ausnahme entfernen",
  "features.remove.body":
    "Die Ausnahme „{scope} · {target}: {value}“ von {feature} entfernen. Danach gilt wieder die breitere Ebene oder der Standardwert.",
  "features.remove.submit": "Entfernen",

  // ── Règles ──
  "rules.loading": "Regeln werden geladen…",
  "rules.loadFailed": "Die Regeln konnten nicht geladen werden.",
  "rules.retry": "Erneut versuchen",
  "rules.pick": "Wählen Sie eine Regel, um ihren Verlauf zu sehen.",
  "rules.notFound": "Diese Regel gibt es nicht. Wählen Sie eine aus der Liste.",
  "rules.next": "ab {date}: {value}",
  "rules.now": "Gilt jetzt",
  "rules.default": "Standardwert",
  "rules.kind.gnf": "Betrag in GNF",
  "rules.kind.minutes": "Dauer in Minuten",
  "rules.kind.hours": "Dauer in Stunden",
  "rules.kind.days": "Dauer in Tagen",
  "rules.kind.km": "Entfernung in km",
  "rules.kind.number": "Zahl",
  "rules.kind.boolean": "ja oder nein",
  "rules.kind.choice": "Auswahl aus einer Liste",
  "rules.kind.text": "Text",
  "rules.sensitivity.open": "Sensibilität {level}: Jede Person, die Regeln bearbeiten darf, kann eine Version anlegen.",
  "rules.sensitivity.critical": "Kritische Sensibilität: Nur ein Inhaber kann eine Version anlegen.",
  "rules.legalBasis.required": "Jede Änderung muss ihre Rechtsgrundlage nennen.",
  "rules.locked.critical":
    "Gesperrt: Diese Regel ist kritisch, nur ein Inhaber kann eine Version anlegen. Ihre Rollen ({roles}) machen Sie nicht zum Inhaber; der Server würde ebenfalls ablehnen.",
  "rules.locked.retired": "Diese Regel ist stillgelegt: Nichts liest sie mehr, sie kann sich nicht mehr ändern.",
  "rules.history": "Versionsverlauf",
  "rules.history.none": "Keine Versionen: Der Standardwert ({value}) gilt.",
  "rules.col.version": "Versionsnr.",
  "rules.col.value": "Wert",
  "rules.col.effective": "Gültig",
  "rules.col.reason": "Begründung",
  "rules.col.legalBasis": "Rechtsgrundlage",
  "rules.col.by": "Von",
  "rules.status.scheduled": "Ab {date}",
  "rules.status.current": "Aktuell",
  "rules.status.since": "seit {date}",
  "rules.status.past": "Ersetzt",
  "rules.new": "Neue Version",
  "rules.form.title": "Neue Version",
  "rules.form.value": "Neuer Wert",
  "rules.form.value.gnf": "Neuer Betrag (GNF)",
  "rules.form.value.minutes": "Neuer Wert (in Minuten)",
  "rules.form.value.hours": "Neuer Wert (in Stunden)",
  "rules.form.value.days": "Neuer Wert (in Tagen)",
  "rules.form.effective": "Gültig ab",
  "rules.form.now": "Sofort",
  "rules.form.at": "Zu einem Datum und einer Uhrzeit",
  "rules.form.atLabel": "Gültig ab dem",
  "rules.form.reason": "Begründung (Pflicht)",
  "rules.form.legalBasis": "Rechtsgrundlage (Pflicht)",
  "rules.form.legalBasisOptional": "Rechtsgrundlage (falls zutreffend)",
  "rules.form.legalBasisPlaceholder": "Gesetz, Vertrag, Rechtsgutachten",
  "rules.form.version":
    "Basiert auf Version {version} der Regel: Wird inzwischen eine andere Version angelegt, wird dies abgelehnt.",
  "rules.form.submit": "Version anlegen",
  "rules.form.cancel": "Abbrechen",
  "rules.form.sending": "Wird gesendet…",
  "rules.value.true": "Ja",
  "rules.value.false": "Nein",
  "rules.problem.value": "Geben Sie einen Wert ein.",
  "rules.problem.number": "Geben Sie eine Zahl ein, zum Beispiel 750000.",
  "rules.problem.integer": "Geben Sie eine ganze Zahl ein.",
  "rules.problem.min": "Der Wert muss mindestens {min} sein.",
  "rules.problem.max": "Der Wert darf höchstens {max} sein.",
  "rules.problem.choice": "Wählen Sie einen Wert aus der Liste.",
  "rules.problem.tooLong": "Höchstens {max} Zeichen.",
  "rules.problem.effectiveMissing": "Wählen Sie Datum und Uhrzeit des Inkrafttretens.",
  "rules.problem.effectivePast":
    "Eine Version kann nicht rückwirkend gelten: Was damals galt, bleibt dokumentiert. Wählen Sie jetzt oder später.",
  "rules.problem.reason": "Geben Sie eine kurze Begründung an.",
  "rules.problem.legalBasis": "Nennen Sie das Gesetz, den Vertrag oder das Rechtsgutachten hinter dieser Änderung.",
  "rules.refused.critical": "Abgelehnt: Diese Regel ist kritisch, nur ein Inhaber kann eine Version anlegen.",
  "rules.refused.effectivePast": "Abgelehnt: Dieses Datum liegt in der Vergangenheit. Wählen Sie jetzt oder später.",
  "rules.refused.legalBasis":
    "Abgelehnt: Diese Regel erfordert eine Rechtsgrundlage. Nennen Sie Gesetz, Vertrag oder Rechtsgutachten.",
  "rules.refused.invalidValue": "Abgelehnt: Dieser Wert passt nicht zur Regel. Prüfen Sie Einheit und Minimum.",
  "rules.refused.retired": "Abgelehnt: Diese Regel ist stillgelegt, nichts liest sie mehr.",
  "rules.stale":
    "Inzwischen wurde eine andere Version dieser Regel angelegt. Laden Sie neu, um sie zu sehen, und versuchen Sie es erneut.",
  "rules.done.now": "Version {version} angelegt: Sie gilt ab sofort.",
  "rules.done.scheduled": "Version {version} angelegt: Sie gilt ab {date}.",

  "sub.team": "Jede Rolle ist eine Liste von Funktionen. Niemand kann sich selbst etwas gewähren.",

  "sub.audit": "Alles, was getan wurde, von wem, für wen · Ihr Aufruf wird selbst protokolliert",

  // ── Audit log ──
  "audit.filter.subject": "Betroffene Person",
  "audit.filter.subjectPlaceholder": "Konto-ID",
  "audit.filter.staff": "Teammitglied",
  "audit.filter.staffPlaceholder": "ID des Mitglieds",
  "audit.filter.action": "Aktion",
  "audit.filter.area": "Bereich",
  "audit.filter.outcome": "Ergebnis",
  "audit.filter.from": "Von",
  "audit.filter.to": "Bis (einschließlich)",
  "audit.filter.all": "Alle",
  "audit.filter.allActions": "Alle",
  "audit.filter.clear": "Filter zurücksetzen",
  "audit.problem.id": "Eine ID hat die Form 3f2a…-…-…-…-… (UUID). Kopieren Sie sie aus der Kontoseite.",
  "audit.problem.period": "Das Ende des Zeitraums liegt vor seinem Beginn.",
  "audit.problem.needsPerson": "Geben Sie die UUID des Ändernden, die der Person oder beide an.",
  "audit.filtersAria": "Protokollfilter",
  "audit.searchAria": "Suche im Protokoll",
  "audit.scope.user": "Einzelperson",
  "audit.scope.allStaff": "Alle Mitarbeiter",
  "audit.scope.allClients": "Alle Kunden",
  "audit.scope.allPros": "Alle Profis",
  "audit.scope.staffRole": "Rolle",
  "audit.scope.proProfile": "Profi-Profil",
  "audit.scope.region": "Region",
  "audit.scope.appVersion": "App-Version",
  "audit.scope.system": "System",
  "audit.scope.staffMember": "Einzelperson",
  "audit.filter.involving": "Betrifft",
  "audit.filter.updaterUuid": "UUID des Ändernden",
  "audit.filter.userUuid": "UUID der Person",
  "audit.pill.updater": "Ändernder",
  "audit.pill.user": "Person",
  "audit.col.time": "Uhrzeit",
  "audit.col.actor": "Ändernder",
  "audit.col.action": "Einstellung",
  "audit.col.subject": "Betrifft",
  "audit.col.outcome": "Ergebnis",
  "audit.col.reason": "Grund",
  "audit.filter.effect": "Anzeigen",
  "audit.effect.write": "Änderungen",
  "audit.effect.read": "Zugriffe",
  "audit.col.before": "Vorher",
  "audit.col.after": "Nachher",
  "audit.change.arrow": "{before} → {after}",
  "audit.change.field": "{key}: {value}",
  "audit.outcome.succeeded": "Erfolgreich",
  "audit.outcome.denied": "Abgelehnt",
  "audit.outcome.requested": "Angefragt",
  "audit.actor.staff": "Teammitglied",
  "audit.actor.noRoles": "keine Rolle",
  "audit.actor.customer": "Kunde",
  "audit.actor.self": "Kunde (selbst)",
  "audit.actor.system": "System",
  "audit.subject.onBehalf": "im Namen von {id}",
  "audit.filterBy": "Nach {id} filtern",
  "audit.subject.open": "Konto von {name} öffnen",
  "audit.masked": "verborgen",
  "audit.sensitive": "🔒 sensibel",
  "audit.loading": "Protokoll wird geladen…",
  "audit.loadFailed": "Das Protokoll konnte nicht geladen werden.",
  "audit.retry": "Erneut versuchen",
  "audit.empty": "Keine Einträge entsprechen diesen Filtern.",
  "audit.emptyForPerson": "Kein Eintrag für diese Suche.",
  "audit.fixProblems": "Korrigieren Sie die markierten Filter, um das Protokoll anzuzeigen.",
  "audit.loadMore": "Mehr laden",
  "audit.loadingMore": "Wird geladen…",
  "audit.moreFailed": "Die nächste Seite konnte nicht geladen werden. Die bereits angezeigten Einträge bleiben gültig.",
  "audit.count": "{count} Einträge angezeigt",
  "audit.countOne": "1 Eintrag angezeigt",
  "audit.end": "Ende des Protokolls für diese Filter.",
  "audit.recorded": "Ihr Aufruf wird selbst protokolliert (VIEW_AUDIT_LOG_STAFF_ACTION), mit den verwendeten Filtern.",
  "audit.sensitiveShown": "Sie sehen die sensiblen Werte (VIEW_SENSITIVE_AUDIT_VALUES).",
  "audit.sensitiveHidden":
    "Sensible Werte sind verborgen: Dafür ist VIEW_SENSITIVE_AUDIT_VALUES nötig, das nur der Inhaber gewährt.",
  "audit.deniedNote": "Eine Zeile „Abgelehnt“ zeigt, dass auch ein Versuch ohne Berechtigung protokolliert wird.",
  "area.account": "Konto",
  "area.access": "Zugang",
  "area.onboarding": "Profi-Registrierung",
  "area.discovery": "Suche",
  "area.jobs": "Anfragen",
  "area.leads": "Leads",
  "area.messaging": "Nachrichten",
  "area.reviews": "Bewertungen",
  "area.payments": "Zahlungen",
  "area.support": "Fälle",
  "area.safety": "Sicherheit",
  "area.settings": "Einstellungen",
  "area.staff": "Team",
  "area.audit": "Audit",

  // ── Roles & team ──
  "team.members": "Team",
  "team.col.name": "Name",
  "team.col.roles": "Rollen",
  "team.col.status": "Status",
  "team.col.lastSeen": "Letzte Anmeldung",
  "team.you": "Sie",
  "team.noRoles": "Keine Rolle",
  "team.status.active": "Aktiv",
  "team.status.invited": "Eingeladen",
  "team.status.deactivated": "Deaktiviert",
  "team.sessions": "{count} offene Sitzungen",
  "team.sessionsOne": "1 offene Sitzung",
  "team.lastSeen.never": "Nie angemeldet",
  "team.lastSeen.today": "heute um {time}",
  "team.lastSeen.yesterday": "gestern um {time}",
  "team.lastSeen.date": "am {date}",
  "team.invite": "Einladen",
  "team.manageRoles": "Rollen",
  "team.endSessions": "Sitzung beenden",
  "team.deactivate": "Deaktivieren",
  "team.loading": "Team wird geladen…",
  "team.loadFailed": "Das Team konnte nicht geladen werden.",
  "team.retry": "Erneut versuchen",
  "team.emptyStaff": "Noch niemand im Team.",
  "team.grid": "Rollen × Funktionen",
  "team.grid.feature": "Funktion",
  "team.grid.editable": "Klicken zum Gewähren oder Entziehen · „critical“ erfordert den Inhaber",
  "team.grid.readOnly": "🔒 Nur lesen · Ändern erfordert MANAGE_ROLES oder ASSIGN_ROLES",
  "team.grid.members": "{count} Mitglieder",
  "team.grid.membersOne": "1 Mitglied",
  "team.grid.yourRole": "🔒 Ihre Rolle",
  "team.grid.newRole": "Neue Rolle",
  "team.grid.editRole": "Bearbeiten",
  "team.grid.noRoles": "Es gibt noch keine Rolle.",
  "team.lock.ownRole": "Sie haben diese Rolle: Niemand ändert, was die eigene Rolle gewährt.",
  "team.lock.critical": "Kritisch: Nur der Inhaber kann sie gewähren oder entziehen.",
  "team.lock.high": "Hohe Sensibilität: Dafür sind MANAGE_ROLES und diese Funktion selbst nötig.",
  "team.lock.low": "Dafür ist MANAGE_ROLES oder ASSIGN_ROLES nötig.",
  "team.lock.criticalShort": "🔒 Inhaber",
  "team.lock.highShort": "🔒 außerhalb Ihrer Rechte",
  "team.cell.grant": "{feature} der Rolle {role} gewähren",
  "team.cell.revoke": "{feature} der Rolle {role} entziehen",
  "team.sensitivity.low": "low",
  "team.sensitivity.high": "high",
  "team.sensitivity.critical": "critical",

  "team.reason": "Begründung",
  "team.reasonHint": "Wird im Audit-Protokoll gespeichert. Ein paar Worte genügen.",
  "team.reasonTooShort": "Geben Sie eine Begründung mit mindestens 3 Zeichen an.",
  "team.cancel": "Abbrechen",
  "team.close": "Schließen",
  "team.sending": "Wird gesendet…",
  "team.reload": "Neu laden",
  "team.refused": "Abgelehnt: {message}",
  "team.stale":
    "Jemand hat dies inzwischen geändert. Laden Sie neu, um die aktuelle Version zu sehen, und versuchen Sie es erneut.",

  "team.grant.title": "Funktion gewähren",
  "team.revoke.title": "Funktion entziehen",
  "team.grant.body": "{feature} der Rolle {role} gewähren ({members}).",
  "team.revoke.body": "{feature} der Rolle {role} entziehen ({members}).",
  "team.grant.submit": "Gewähren",
  "team.revoke.submit": "Entziehen",
  "team.grant.done": "{feature} der Rolle {role} gewährt.",
  "team.revoke.done": "{feature} der Rolle {role} entzogen.",
  "team.critical.note": "Kritische Funktion: Die Inhaber werden benachrichtigt.",

  "team.invite.title": "Teammitglied einladen",
  "team.invite.name": "Vollständiger Name",
  "team.invite.email": "Geschäftliche E-Mail",
  "team.invite.phone": "Telefon",
  "team.invite.phoneHint": "Internationales Format, z. B. +224621234567.",
  "team.invite.roles": "Rollen",
  "team.invite.noRoles": "Legen Sie zuerst eine Rolle an — Sie können jetzt einladen und Rollen später vergeben.",
  "team.invite.rolesHint": "Ohne Rolle öffnet sich eine Konsole, in der nichts zugänglich ist.",
  "team.invite.sms":
    "Ein 6-stelliger Code wird per SMS an diese Nummer gesendet. Er ist {hours} Stunden gültig und dient dazu, auf der Seite „Mein Passwort festlegen“ ein Passwort zu wählen.",
  "team.invite.submit": "Einladung senden",
  "team.invite.done": "{name} ist eingeladen. Code per SMS gesendet, gültig bis {until}.",
  "team.invite.missing": "Geben Sie Name, E-Mail und Telefon an.",

  "team.deactivate.title": "{name} deaktivieren",
  "team.deactivate.body":
    "{name} kann sich nie wieder anmelden. Offene Sitzungen und eine eventuelle Einladung enden sofort. Ein deaktiviertes Mitglied wird nicht gelöscht.",
  "team.deactivate.submit": "Deaktivieren",
  "team.deactivate.done": "{name} ist deaktiviert. Beendete Sitzungen: {count}.",
  "team.reactivate": "Reaktivieren",
  "team.reactivate.title": "{name} reaktivieren",
  "team.reactivate.body":
    "{name} kann sich wieder anmelden, mit den bisherigen Rollen. Die Anmeldung beginnt von vorn: keine Sitzung und kein Code wird wiederhergestellt.",
  "team.reactivate.submit": "Reaktivieren",
  "team.reactivate.done": "{name} ist wieder aktiv.",

  "team.endSessions.title": "Sitzungen von {name} beenden",
  "team.endSessions.body": "{name} wird überall abgemeldet ({sessions}).",
  "team.endSessions.self": "Das sind Ihre eigenen Sitzungen: Sie werden ebenfalls abgemeldet.",
  "team.endSessions.submit": "Beenden",
  "team.endSessions.done": "Sitzungen von {name} beendet: {count}.",

  "team.roles.title": "Rollen von {name}",
  "team.roles.held": "Aktuelle Rollen",
  "team.roles.available": "Weitere Rollen",
  "team.roles.none": "Keine",
  "team.roles.assign": "{role} zuweisen",
  "team.roles.remove": "{role} entfernen",
  "team.roles.assignDone": "{role} wurde {name} zugewiesen.",
  "team.roles.removeDone": "{role} wurde {name} entzogen.",
  "team.roles.reasonFirst":
    "Geben Sie zuerst eine Begründung an und wählen Sie dann die Rolle, die zugewiesen oder entfernt werden soll.",

  "team.role.createTitle": "Neue Rolle",
  "team.role.editTitle": "Rolle {name} bearbeiten",
  "team.role.name": "Name der Rolle",
  "team.role.description": "Beschreibung",
  "team.role.descriptionHint": "Optional. Was diese Rolle tut, in einem Satz.",
  "team.role.department": "Abteilung",
  "team.role.noDepartment": "Keine",
  "team.role.departmentHint":
    "Der Bereich, in dem seine Inhaber landen. Was die Rolle darf, entscheiden weiterhin ihre Funktionen.",
  "team.role.create": "Rolle anlegen",
  "team.role.save": "Speichern",
  "team.role.created": "Rolle {name} angelegt. Gewähren Sie ihr Funktionen im Raster.",
  "team.role.saved": "Rolle {name} gespeichert.",
  "team.role.nameTooShort": "Der Name hat mindestens 2 Zeichen.",
  "team.role.unchanged": "Ändern Sie den Namen oder die Beschreibung.",
  "team.role.version": "Version {version}",

  // ── Permissions on a role (ADR-0019) and who can do this ──
  "team.tab.roles": "Rollen & Team",
  "team.tab.who": "Wer darf das",
  "team.tabs.aria": "Bereiche des Teams",
  "team.permission.noLimit": "Kein Limit",
  "team.permission.lockFeature":
    "Gewähren Sie {role} zuerst {feature}: eine Berechtigung gilt nur unter ihrer Funktion.",
  "team.cell.grantPermission": "{permission} der Rolle {role} gewähren",
  "team.cell.editPermission": "{permission} für die Rolle {role} ändern",
  "team.permission.grantTitle": "Berechtigung gewähren",
  "team.permission.editTitle": "Berechtigung ändern",
  "team.permission.grantBody": "{permission} der Rolle {role} ({members}) gewähren.",
  "team.permission.editBody": "Die Rolle {role} ({members}) hat {permission}.",
  "team.permission.under": "Unter {feature}.",
  "team.permission.approvable": "Ohne sie wird ihr Versuch zu einer Freigabeanfrage.",
  "team.permission.refusable": "Ohne sie wird die Handlung abgelehnt.",
  "team.permission.limits": "Limits",
  "team.permission.limitsHint":
    "Das Höchste, was ihre Inhaber in jeder Währung allein tun dürfen. Darüber, oder in einer Währung ohne Limit, wird daraus eine Freigabeanfrage.",
  "team.permission.noLimitHint": "Kein Limit: ihre Inhaber handeln allein, gleich welcher Betrag.",
  "team.permission.currency": "Währung",
  "team.permission.amount": "Betrag",
  "team.permission.addLimit": "Ein Limit in einer anderen Währung hinzufügen",
  "team.permission.removeLimit": "Das Limit in {currency} entfernen",
  "team.permission.limitProblem":
    "Geben Sie für jedes Limit eine Währung aus drei Buchstaben und einen Betrag über null an.",
  "team.permission.duplicateCurrency": "Ein Limit je Währung: {currency} wird zweimal genannt.",
  "team.permission.grantDone": "{permission} der Rolle {role} gewährt.",
  "team.permission.revokeDone": "{permission} der Rolle {role} entzogen.",
  "team.revoke.permissionsToo": "Ihre Berechtigungen gehen mit: {permissions}.",
  "team.refused.sensitivity":
    "Ihr Zugang erlaubt es nicht, dies zu gewähren oder zu entziehen. Fragen Sie einen Ops Manager oder den Inhaber.",
  "team.refused.critical": "Kritisch: nur ein Inhaber, der es selbst hat, gewährt oder entzieht es.",
  "team.refused.limitAboveOwn": "Sie können kein höheres Limit gewähren als Ihr eigenes. Fragen Sie den Inhaber.",
  "team.refused.featureFirst":
    "Diese Berechtigung sitzt auf einer Funktion, die die Rolle nicht gewährt. Gewähren Sie zuerst die Funktion.",
  "team.refused.notLimited": "Diese Berechtigung trägt kein Limit.",
  "team.refused.limitWithoutGrant": "Ein Limit gehört zu einer gewährten Berechtigung.",
  "team.refused.ownRole":
    "Sie haben diese Rolle, also können Sie nicht ändern, was sie gewährt. Fragen Sie einen anderen Inhaber.",
  "team.who.action": "Aktion",
  "team.who.feature": "Funktion",
  "team.who.permission": "Berechtigung",
  "team.who.noPermission": "Keine Berechtigung deckt diese Aktion ab: wer die Funktion hat, darf sie ausführen.",
  "team.who.approvable": "kann angefragt werden",
  "team.who.col.through": "Über",
  "team.who.col.limit": "Limit",
  "team.who.personal": "Für diese Person gesetzt",
  "team.who.mayRequest": "Darf nur anfragen: wird zu einer Freigabeanfrage",
  "team.who.loading": "Wer das darf, wird geladen…",
  "team.who.loadFailed": "Wer das darf, konnte nicht geladen werden.",
  "team.who.empty": "Niemand darf das.",

  // ── Customers & pros ──
  // Trades, by WorkerCategory.
  "trade.electrician": "Elektriker",
  "trade.plumber": "Klempner",
  "trade.mason": "Maurer",
  "trade.carpenter": "Tischler",
  "trade.ac_technician": "Klimatechniker",
  "trade.cleaner": "Reinigungskraft",
  "trade.painter": "Maler",
  "trade.handyman": "Handwerker",
  "trade.laundry": "Wäscherei",
  "trade.babysitter": "Babysitter",
  "trade.fridge_repair": "Kühlschrankreparatur",
  "trade.moto_driver": "Motorradtaxi",
  "trade.tailor": "Schneider",
  "trade.car_mechanic": "Kfz-Mechaniker",
  "trade.welder": "Schweißer",
  "trade.gardener": "Gärtner",
  "trade.phone_repair": "Handyreparatur",
  "trade.tutor": "Nachhilfelehrer",
  "trade.hairdresser": "Friseur",
  "trade.security_guard": "Wachmann",
  "trade.other": "Sonstiges",
  // A number with its unit (the settings' Unit).
  "unit.km": "{value} km",
  "unit.minutes": "{value} Min.",
  "unit.hours": "{value} Std.",
  "unit.days": "{value} Tg.",
  "unit.seconds": "{value} Sek.",

  "customers.search.label": "Einen Kunden oder Profi suchen",
  "customers.search.placeholder": "Name, Telefon, E-Mail oder Konto-ID",
  "customers.search.hint": "Z. B. „621 23 45 67“, „Sékou Touré“, „mariama.bah@“ oder die Konto-ID.",
  "customers.search.submit": "Suchen",
  "customers.search.tooShort": "Geben Sie mindestens 2 Zeichen ein.",
  "customers.search.locked": "Die Suche erfordert",
  "customers.search.idle": "Geben Sie einen Namen, eine Nummer, eine E-Mail oder eine ID ein, um ein Konto zu finden.",
  "customers.search.loading": "Suche läuft…",
  "customers.search.loadFailed": "Die Suche ist fehlgeschlagen.",
  "customers.search.empty": "Kein Konto entspricht „{query}“.",
  "customers.search.matchedBy.userId": "Suche nach Konto-ID",
  "customers.search.matchedBy.email": "Suche nach E-Mail",
  "customers.search.matchedBy.phone": "Suche nach Telefon",
  "customers.search.matchedByPhone": "Suche nach Telefon: {phone}",
  "customers.search.matchedBy.name": "Suche nach Name",
  "customers.search.count": "{count} Konten angezeigt",
  "customers.search.countOne": "1 Konto angezeigt",
  "customers.search.end": "Ende der Ergebnisse.",
  "customers.search.loadMore": "Mehr laden",
  "customers.search.loadingMore": "Wird geladen…",
  "customers.search.moreFailed":
    "Die nächste Seite konnte nicht geladen werden. Die bereits angezeigten Konten bleiben gültig.",
  "customers.search.retry": "Erneut versuchen",
  "customers.search.masked":
    "Nummern und E-Mails verborgen. „Anzeigen“ auf der Kontoseite, mit Begründung, wird protokolliert.",
  "customers.col.name": "Name",
  "customers.col.contact": "Kontakt",
  "customers.col.kind": "Kunde oder Profi",
  "customers.col.region": "Region",
  "customers.col.since": "Mitglied seit",
  "customers.deleted": "Konto gelöscht",
  "customers.deletedName": "Name gelöscht",
  "customers.kind.client": "Kunde",
  "customers.kind.pro": "Profi",
  "customers.kind.proAndClient": "Profi und Kunde",
  "customers.kind.admin": "Administrator",
  "customers.region.gn": "Guinea",
  "customers.region.sn": "Senegal",
  "customers.region.us": "Vereinigte Staaten",
  "customers.region.eu": "Europa",
  "customers.verification.unverified": "Nicht geprüft",
  "customers.verification.pending": "Ausstehend",
  "customers.verification.verified": "Geprüft",
  "customers.verification.rejected": "Abgelehnt",

  "customers.back": "← Suche",
  "customers.page.loading": "Konto wird geladen…",
  "customers.page.loadFailed": "Das Konto konnte nicht geladen werden.",
  "customers.page.notFound": "Kein Konto hat diese ID. Suchen Sie die Person erneut.",
  "customers.page.locked": "Die Kontoseite erfordert",
  "customers.page.sub": "{kind} · Mitglied seit {since} · {region}",
  "customers.page.deletedTitle": "Konto gelöscht",
  "customers.page.deletedSub": "Gelöscht am {date} · Name und Kontaktdaten entfernt",

  "customers.reveal.button": "Anzeigen",
  "customers.reveal.locked": "🔒 verborgen",
  "customers.reveal.lockedTitle":
    "Das Anzeigen der Kontaktdaten erfordert REVEAL_CONTACT_DETAILS_STAFF_FEATURE_ENABLEMENT",
  "customers.reveal.title": "Kontaktdaten von {name} anzeigen",
  "customers.reveal.body":
    "Die vollständige Nummer und E-Mail erscheinen nur auf dieser Seite, solange Sie sie ansehen. Das Anzeigen wird mit Ihrer Begründung im Protokoll des Kontos gespeichert.",
  "customers.reveal.reason": "Begründung",
  "customers.reveal.placeholder": "Warum? z. B. Kunden zurückrufen, Fall D-0142",
  "customers.reveal.tooShort": "Geben Sie eine Begründung mit mindestens 3 Zeichen an.",
  "customers.reveal.submit": "Anzeigen",
  "customers.reveal.sending": "Wird gesendet…",
  "customers.reveal.cancel": "Abbrechen",
  "customers.reveal.noAnswer":
    "Keine Antwort vom Server: Das Anzeigen wurde möglicherweise protokolliert, ohne dass die Kontaktdaten bei Ihnen ankamen. Versuchen Sie es erneut; auch ein neues Anzeigen wird protokolliert.",
  "customers.reveal.recorded": "Angezeigt um {time} · Aufruf im Protokoll des Kontos gespeichert.",
  "customers.reveal.hide": "Verbergen",
  "customers.reveal.phone": "Telefon:",
  "customers.reveal.email": "E-Mail:",
  "customers.reveal.none": "keine",

  "customers.obo.title": "Im Namen von {name} handeln",
  "customers.obo.body":
    "Ein Code wird per SMS an die Nummer des Kontos gesendet. Der Kunde liest ihn laut vor; die Sitzung dauert 30 Minuten, und jede Aktion wird mit seinem und Ihrem Namen protokolliert.",
  "customers.obo.send": "Code senden",
  "customers.obo.soon": "Bald verfügbar",

  "customers.tabs.aria": "Bereiche des Kontos",
  "customers.tab.overview": "Überblick",
  "customers.tab.subscription": "Abo",
  "customers.tab.settings": "Funktionen & Einstellungen",
  "customers.tab.jobs": "Anfragen",
  "customers.tab.audit": "Protokoll",

  "customers.pro.title": "Profi-Profil",
  "customers.pro.none": "Kein Profi-Profil.",
  "customers.pro.identity": "Identität",
  "customers.pro.approval": "Freigabe",
  "customers.pro.rating": "Bewertung",
  "customers.pro.ratingValue": "{rating} ★ · {count} Bewertungen",
  "customers.pro.noReviews": "Keine Bewertungen",
  "customers.pro.rate": "Preis",
  "customers.pro.subscription": "Abo",
  "customers.pro.noSubscription": "Keines",
  "customers.pro.subscriptionLine": "{plan} · {status}",
  "customers.pro.paidUntil": "{plan} · bezahlt bis {date}",
  "customers.pro.endingUntil": "{plan} · aktiv bis {date}, läuft aus",
  "customers.pro.approval.pending": "Ausstehend",
  "customers.pro.approval.approved": "Freigegeben",
  "customers.pro.approval.rejected": "Abgelehnt",
  "customers.subscription.pending": "ausstehend",
  "customers.subscription.active": "aktiv",
  "customers.subscription.ending": "läuft aus",
  "customers.subscription.suspended": "gesperrt",
  "customers.subscription.ended": "beendet",
  "customers.rate.hourly": "{amount} / Stunde",
  "customers.rate.daily": "{amount} / Tag",
  "customers.rate.fixed": "{amount} pauschal",
  "customers.client.title": "Kundenprofil",
  "customers.client.jobsPosted": "Veröffentlichte Anfragen",
  "customers.client.lastActivity": "Letzte Aktivität",
  "customers.client.never": "Keine",
  "customers.restrictions.title": "Aktive Einschränkungen",
  "customers.restrictions.none": "Keine Einschränkung.",
  "customers.restrictions.suspension": "Vorläufige Sperre",
  "customers.restrictions.line": "{feature} deaktiviert für {scope} · Begründung: {reason}",
  "customers.restrictions.until": "bis {date}",

  "customers.settings.col.setting": "Einstellung",
  "customers.settings.col.value": "Wert",
  "customers.settings.col.source": "Ebene",
  "customers.settings.col.reason": "Begründung",
  "customers.settings.col.expires": "Läuft ab",
  "customers.settings.on": "Aktiviert",
  "customers.settings.off": "Deaktiviert",
  "customers.settings.restricted": "Eingeschränkt",
  "customers.settings.none": "Für dieses Konto gilt keine Einstellung.",
  "customers.settings.edit": "Einstellung ändern",
  "customers.settings.editSoon":
    "Bald verfügbar: Das Ändern der Einstellungen eines Kunden kommt in einer der nächsten Versionen.",
  "customers.settings.foot":
    "Die genaueste Ebene gilt: Person, Profi-Profil, alle Profis oder alle Kunden, Region, System, dann der Standardwert.",
  "customers.source.user": "Person",
  "customers.source.proProfile": "Profi-Profil",
  "customers.source.allPros": "Alle Profis",
  "customers.source.allClients": "Alle Kunden",
  "customers.source.region": "Region",
  "customers.source.regionOf": "Region {region}",
  "customers.source.appVersion": "App-Version",
  "customers.source.system": "System",
  "customers.source.default": "Standard",
  "customers.scope.user": "die Person",
  "customers.scope.proProfile": "das Profi-Profil",

  "customers.jobs.col.trade": "Beruf",
  "customers.jobs.col.type": "Art",
  "customers.jobs.col.side": "Rolle",
  "customers.jobs.col.other": "Mit",
  "customers.jobs.col.status": "Status",
  "customers.jobs.col.date": "Datum",
  "customers.jobs.type.direct": "Direkt",
  "customers.jobs.type.open": "Offen",
  "customers.jobs.side.client": "Kunde",
  "customers.jobs.side.pro": "Profi",
  "customers.jobs.status.requested": "Angefragt",
  "customers.jobs.status.quoted": "Angebot gesendet",
  "customers.jobs.status.accepted": "Vermittelt",
  "customers.jobs.status.cancelled": "Storniert",
  "customers.jobs.noPro": "Kein Profi gewählt",
  "customers.jobs.deletedParty": "Konto gelöscht",
  "customers.jobs.none": "Keine Anfragen für dieses Konto.",
  "customers.jobs.foot": "Höchstens die letzten {count} Anfragen, als Kunde oder als Profi.",

  "customers.journal.recorded": "Ihr Aufruf dieses Protokolls wird selbst protokolliert.",
  "customers.journal.locked": "Das Audit-Protokoll erfordert",
  "customers.lockedRoles": "Ihre Rollen ({roles}) enthalten sie nicht.",
  "customers.journal.onBehalf": "im Namen von {id}",
  "customers.journal.actorWithRoles": "{name} ({roles})",
  "customers.journal.reason": "Begründung: {reason}",
  "customers.journal.empty": "Für dieses Konto ist noch nichts protokolliert.",
  "customers.journal.all": "Im Audit-Protokoll öffnen →",
  "customers.journal.denied": "abgelehnt",
  "customers.journal.succeeded": "erfolgreich",

  // ── Set my password (public) ──
  "setPassword.title": "Mein Passwort festlegen",
  "setPassword.subtitle": "Verwenden Sie den 6-stelligen Code, den Sie mit Ihrer Einladung per SMS erhalten haben.",
  "setPassword.email": "Geschäftliche E-Mail",
  "setPassword.code": "Per SMS erhaltener Code",
  "setPassword.password": "Neues Passwort",
  "setPassword.confirm": "Passwort bestätigen",
  "setPassword.rules": "Das Passwort muss:",
  "setPassword.rule.length": "mindestens {min} Zeichen lang sein",
  "setPassword.rule.tooLong": "höchstens {max} Zeichen lang sein",
  "setPassword.rule.mix": "einen Buchstaben und ein Zeichen enthalten, das kein Buchstabe ist",
  "setPassword.rule.email": "Ihre E-Mail nicht enthalten ({name})",
  "setPassword.rule.emailGeneric": "Ihre E-Mail nicht enthalten",
  "setPassword.mismatch": "Die beiden Passwörter stimmen nicht überein.",
  "setPassword.codeFormat": "Der Code besteht aus 6 Ziffern.",
  "setPassword.emailFormat": "Geben Sie eine gültige E-Mail ein.",
  "setPassword.submit": "Mein Passwort speichern",
  "setPassword.submitting": "Wird gespeichert…",
  "setPassword.back": "Zurück zur Anmeldung",
  "setPassword.done": "Passwort gespeichert. Melden Sie sich mit Ihrer E-Mail und diesem Passwort an.",
  "signIn.setPassword": "Eingeladen? Mein Passwort festlegen",
  "signIn.or": "oder",
  "signIn.heading": "Walliyo Konsole",
  "signIn.forgot": "Passwort vergessen?",
  "signIn.environment": "Umgebung",
  "signIn.build": "Build",
  "signIn.googleUnavailable":
    "Die Google-Anmeldung ist nicht verfügbar. Nutzen Sie E-Mail und Passwort oder sagen Sie dem Owner Bescheid.",
  "error.setPassword.invalid_code":
    "Diese E-Mail und dieser Code passen zu keiner offenen Einladung. Prüfen Sie beides; ist der Code verloren oder abgelaufen, lassen Sie sich erneut einladen.",
  "error.setPassword.too_many_attempts":
    "Zu viele falsche Codes: Dieser Code ist nicht mehr gültig. Lassen Sie sich erneut einladen.",
  "error.setPassword.validation_error":
    "Das Passwort erfüllt die Regeln nicht, oder der Code hat nicht das richtige Format. Korrigieren Sie es und versuchen Sie es erneut.",

  // ── Dossiers (the support case queue) ──
  "cases.newCall": "+ Anruf erfassen",
  "cases.queue.aria": "Fall-Warteschlange",
  "cases.queue.views": "Ansichten der Warteschlange",
  "cases.view.all": "Alle",
  "cases.view.unassigned": "Nicht zugewiesen",
  "cases.view.mine": "Meine",
  "cases.view.late": "Überfällig",
  "cases.filter.status": "Status",
  "cases.filter.notClosed": "Alle außer geschlossenen",
  "cases.filter.channel": "Kanal",
  "cases.filter.anyChannel": "Alle Kanäle",
  "cases.filter.customer": "Kunde {id}",
  "cases.filter.clear": "Kundenfilter entfernen",
  "cases.col.case": "Fall",
  "cases.col.deadline": "Frist",
  "cases.queue.loading": "Warteschlange wird geladen…",
  "cases.queue.loadFailed": "Die Warteschlange konnte nicht geladen werden.",
  "cases.queue.empty": "Kein Fall entspricht diesen Filtern.",
  "cases.loadMore": "Mehr laden",
  "cases.loadingMore": "Wird geladen…",
  "cases.retry": "Erneut versuchen",
  "cases.pick": "Wählen Sie einen Fall in der Warteschlange, um seinen Verlauf zu sehen.",
  "cases.loading": "Fall wird geladen…",
  "cases.loadFailed": "Dieser Fall konnte nicht geladen werden.",
  "cases.notFound": "Kein Fall hat diese ID. Vielleicht wurde sie falsch kopiert.",
  "cases.refreshFailed": "Der Fall konnte nicht aktualisiert werden: Die Anzeige ist möglicherweise veraltet.",
  "cases.deadline.late": "Überfällig · {time}",
  "cases.deadline.left": "noch {time}",
  "cases.deadline.replied": "Beantwortet",
  "cases.deadline.closed": "Geschlossen",
  "cases.channel.phone": "Anruf",
  "cases.channel.email": "E-Mail",
  "cases.channel.chat": "App-Chat",
  "cases.status.open": "Offen",
  "cases.status.waiting_on_customer": "Wartet auf Kunden",
  "cases.status.closed": "Geschlossen",
  "access.mine.title": "Was Sie angefragt haben",
  "access.mine.none": "Sie haben nichts angefragt.",
  "access.mine.what": "Was",
  "access.mine.state": "Wie weit es ist",
  "access.mine.asked": "Angefragt",
  "access.mine.withWhom": "Bei",
  "access.mine.waiting": "Wartet",
  "access.mine.granted": "Gewährt",
  "access.mine.refused": "Abgelehnt",
  "access.mine.overdue": "Überfällig",
  "access.mine.nobody": "Noch niemand",
  "access.mine.answer": "Antwort",
  "access.mine.loading": "Ihre Anfragen werden geladen…",
  "access.mine.loadFailed": "Ihre Anfragen konnten nicht gelesen werden.",
  "access.ask.title": "Mehr anfragen",
  "access.ask.explain":
    "Etwas zu bauen ist kein Grund, die Daten darin zu halten. Fragen Sie nach dem Kleinsten, das Sie fertig werden lässt, so lange Sie es brauchen. Es geht an jemanden, der es gewähren kann, wird Ihnen allein gewährt und endet von selbst.",
  "access.ask.what": "Was Sie brauchen",
  "access.ask.until": "Bis",
  "access.ask.why": "Warum Sie es brauchen",
  "access.ask.whyHint": "Was Sie tun, und warum das das Kleinste ist, das es ermöglicht",
  "access.ask.send": "Anfrage senden",
  "access.ask.pickOne": "Wählen Sie, was Sie brauchen",
  "access.ask.pastDate": "Nennen Sie ein Datum in der Zukunft, oder lassen Sie es offen.",
  "access.ask.sent": "Gesendet. Es ist Fall {reference}.",
  "cases.kind.access": "Zugriffsanfrage",
  "cases.kind.customer": "Kunde",
  "cases.kind": "Art",
  "cases.access.wants": "Bittet um",
  "cases.access.until": "Bis",
  "cases.access.grant": "Gewähren",
  "cases.access.refuse": "Ablehnen",
  "cases.access.own": "Sie können Ihre eigene Anfrage nicht beantworten.",
  "cases.access.explain":
    "Nur dieser Person gewährt, nie ihrem Team. Es endet am erbetenen Datum, und jede berechtigte Person kann es vorher beenden.",
  "cases.priority": "Priorität: {priority}",
  "cases.priority.low": "Niedrig",
  "cases.priority.normal": "Normal",
  "cases.priority.high": "Hoch",
  "cases.priority.urgent": "Dringend",
  "cases.unknownCaller": "Unbekannter Anrufer",
  "cases.unknownCallerPhone": "Unbekannter Anrufer · {phone}",
  "cases.customerWithPhone": "{name} · {phone}",
  "cases.ownedBy": "zugewiesen an {name}",
  "cases.unassigned": "nicht zugewiesen",
  "cases.closedBecause": "Geschlossen · Begründung: {reason}",
  "cases.timeline.aria": "Verlauf des Falls",
  "cases.timeline.empty": "Zu diesem Fall ist noch nichts erfasst.",
  "cases.internal": "Intern",
  "cases.entry.call": "Anruf",
  "cases.entry.callWithDuration": "Anruf ({duration})",
  "cases.entry.note": "Interne Notiz",
  "cases.entry.chatIn": "Nachricht des Kunden in der App",
  "cases.entry.chatOut": "Antwort in der App",
  "cases.entry.smsIn": "SMS des Kunden",
  "cases.entry.smsOut": "Antwort per SMS",
  "cases.entry.smsOutTo": "SMS an {phone}",
  "cases.entry.emailIn": "E-Mail des Kunden",
  "cases.entry.emailOut": "Antwort per E-Mail",
  "cases.sms.pending": "Wird gesendet",
  "cases.sms.sent": "Gesendet",
  "cases.sms.failed": "Nicht gesendet",
  "cases.action.claim": "Übernehmen",
  "cases.action.reassign": "Neu zuweisen",
  "cases.action.link": "Mit einem Kunden verknüpfen",
  "cases.action.relink": "Kunden ändern",
  "cases.action.close": "Schließen",
  "cases.needs": "Erfordert {feature}",
  "cases.sending": "Wird gesendet…",
  "cases.cancel": "Abbrechen",
  "cases.reload": "Fall neu laden",
  "cases.reason": "Begründung",
  "cases.reasonHint": "Wird mit Ihrem Namen im Audit-Protokoll gespeichert.",
  "cases.stale":
    "Dieser Fall wurde geändert, während Sie ihn angesehen haben. Laden Sie ihn neu, prüfen Sie die Änderung und versuchen Sie es erneut: Ihr Text bleibt erhalten.",
  "cases.claim.done": "{reference} gehört jetzt Ihnen.",
  "cases.claim.taken": "Zu spät: {name} hat diesen Fall gerade übernommen. Wählen Sie einen anderen.",
  "cases.composer.aria": "Zum Fall schreiben",
  "cases.composer.reply": "Antworten",
  "cases.composer.note": "Interne Notiz",
  "cases.composer.call": "Anruf erfassen",
  "cases.reply.noChannel":
    "Kein Kundenkonto verknüpft: Verknüpfen Sie den Kunden, um in der App zu antworten, oder rufen Sie ihn zurück.",
  "cases.reply.notified": "Der Kunde erhält eine Benachrichtigung in der App. Nie eine SMS.",
  "cases.reply.label": "Antwort an {name}",
  "cases.reply.sendChat": "In der App senden",
  "cases.reply.doneChat": "Antwort in der App gesendet.",
  "cases.reply.closed": "Dieser Fall ist geschlossen: Er nimmt keine Antworten mehr an.",
  "cases.reply.unassigned": "Übernehmen Sie diesen Fall, um dem Kunden zu antworten.",
  "cases.reply.notOwner": "Nur die zuständige Person antwortet dem Kunden. Bitten Sie bei Bedarf um eine Neuzuweisung.",
  "cases.refused.claimed": "Zu spät: Jemand anderes hat diesen Fall gerade übernommen. Wählen Sie einen anderen.",
  "cases.refused.closed": "Dieser Fall wurde inzwischen geschlossen. Laden Sie ihn neu.",
  "cases.refused.notOwner":
    "Dieser Fall gehört jetzt jemand anderem. Laden Sie ihn neu oder bitten Sie um Neuzuweisung.",
  "cases.refused.noCustomer":
    "Kein Kundenkonto verknüpft: Verknüpfen Sie den Kunden, um in der App zu antworten, oder rufen Sie ihn zurück.",
  "cases.note.hint": "Nur für das Team sichtbar, nie für den Kunden.",
  "cases.note.label": "Interne Notiz",
  "cases.note.submit": "Notiz hinzufügen",
  "cases.note.done": "Interne Notiz hinzugefügt.",
  "cases.call.label": "Was gesagt wurde (interne Zusammenfassung)",
  "cases.call.minutes": "Dauer (Minuten)",
  "cases.call.submit": "Anruf erfassen",
  "cases.call.done": "Anruf erfasst.",
  "cases.reassign.title": "{reference} neu zuweisen",
  "cases.reassign.from": "Derzeit zugewiesen an {name}.",
  "cases.reassign.unassigned": "Dieser Fall ist noch niemandem zugewiesen.",
  "cases.reassign.to": "Neue zuständige Person",
  "cases.reassign.choose": "Teammitglied wählen",
  "cases.reassign.loading": "Team wird geladen…",
  "cases.reassign.toId": "ID des Teammitglieds",
  "cases.reassign.idHint": "Ohne Zugriff auf die Teamliste fügen Sie die ID des Mitglieds ein.",
  "cases.reassign.submit": "Neu zuweisen",
  "cases.reassign.done": "Fall an {name} neu zugewiesen.",
  "cases.close.title": "{reference} schließen",
  "cases.close.body":
    "Der Kunde kann nicht mehr darauf antworten; meldet er sich wieder, wird ein neuer Fall eröffnet.",
  "cases.close.submit": "Fall schließen",
  "cases.close.done": "{reference} ist geschlossen.",
  "cases.link.title": "{reference} mit einem Kunden verknüpfen",
  "cases.link.body": "Suchen Sie das Konto des Kunden, der den Support kontaktiert hat.",
  "cases.link.caller": "Der Anrufer hat {phone} angegeben. Suchen Sie sein Konto.",
  "cases.link.current": "Derzeit verknüpft mit {name}.",
  "cases.link.submit": "Verknüpfen",
  "cases.link.done": "Fall mit {name} verknüpft.",
  "cases.customer.search": "Kunden suchen",
  "cases.customer.placeholder": "Name, Telefon, E-Mail oder ID",
  "cases.customer.searchButton": "Suchen",
  "cases.customer.searching": "Suche läuft…",
  "cases.customer.none": "Kein Konto gefunden.",
  "cases.customer.results": "Gefundene Konten",
  "cases.customer.id": "ID des Kundenkontos",
  "cases.customer.idHint": "Die Suche erfordert {feature}: Fügen Sie die Konto-ID ein.",
  "cases.newCall.title": "Anruf erfassen",
  "cases.newCall.who": "Wer hat angerufen?",
  "cases.newCall.known": "Ein Kunde",
  "cases.newCall.unknown": "Ein unbekannter Anrufer",
  "cases.newCall.phone": "Nummer des Anrufers",
  "cases.newCall.phoneHint": "Im internationalen Format, zum Beispiel +224 621 23 45 67.",
  "cases.newCall.subject": "Betreff",
  "cases.newCall.internal": "Intern: Der Kunde sieht diese Zusammenfassung nie.",
  "cases.newCall.priority": "Priorität",
  "cases.newCall.submit": "Fall eröffnen",
  "cases.problem.reason": "Begründen Sie es in wenigen Worten.",
  "cases.problem.body": "Schreiben Sie etwas.",
  "cases.problem.tooLong": "Zu lang: höchstens {max} Zeichen.",
  "cases.problem.minutes": "Die Dauer ist eine ganze Zahl von Minuten, von 0 bis {max}.",
  "cases.problem.phone": "Die Nummer muss im internationalen Format sein, zum Beispiel +224621234567.",
  "cases.problem.pickCustomer": "Wählen Sie den Kunden.",
  "cases.problem.customerId": "Diese Konto-ID hat nicht das richtige Format.",
  "cases.problem.pickStaff": "Wählen Sie, wer den Fall übernimmt.",
  "cases.problem.staffId": "Diese Mitglieds-ID hat nicht das richtige Format.",
  "cases.problem.subject": "Geben Sie dem Fall einen kurzen Betreff.",
  // ── Verifizierungen ──
  "verifications.summary.open": "{count} warten · die älteste seit {age}",
  "verifications.summary.none": "Keine Verifizierung wartet",
  "verifications.summary.shown": "{count} Verifizierungen angezeigt",
  "verifications.filter.status": "Stand",
  "verifications.filter.status.open": "Alle offenen",
  "verifications.filter.status.submitted": "Zu prüfen",
  "verifications.filter.status.flagged": "Gesperrtes Dokument",
  "verifications.filter.status.approved": "Genehmigt",
  "verifications.filter.status.rejected": "Abgelehnt",
  "verifications.filter.document": "Dokumentart",
  "verifications.filter.document.all": "Alle",
  "verifications.filter.document.national_id": "Personalausweis",
  "verifications.filter.document.passport": "Reisepass",
  "verifications.queue.aria": "Warteschlange der Verifizierungen, älteste zuerst",
  "verifications.queue.loading": "Warteschlange wird geladen…",
  "verifications.queue.loadFailed": "Die Warteschlange konnte nicht geladen werden.",
  "verifications.queue.emptyOpen": "Keine Verifizierung wartet: Die Warteschlange ist leer.",
  "verifications.queue.empty": "Keine Verifizierung passt zu diesen Filtern.",
  "verifications.queue.waited": "Wartezeit seit dem Einreichen",
  "verifications.queue.end": "Ende der Warteschlange.",
  "verifications.flag.banned": "Gesperrtes Dokument",
  "verifications.status.submitted": "Zu prüfen",
  "verifications.status.flagged": "Gesperrtes Dokument",
  "verifications.status.approved": "Genehmigt",
  "verifications.status.rejected": "Abgelehnt",
  "verifications.document.national_id": "Personalausweis",
  "verifications.document.passport": "Reisepass",
  "verifications.side.front": "Vorderseite",
  "verifications.side.back": "Rückseite",
  "verifications.side.selfie": "Selfie-Foto",
  "verifications.detail.pick": "Wählen Sie eine Verifizierung in der Warteschlange, um sie zu prüfen.",
  "verifications.detail.loading": "Verifizierung wird geladen…",
  "verifications.detail.loadFailed": "Diese Verifizierung konnte nicht geladen werden.",
  "verifications.detail.notFound": "Keine Verifizierung hat diese ID. Laden Sie die Warteschlange neu.",
  "verifications.detail.noPhone": "ohne Telefon",
  "verifications.detail.submitted": "eingereicht am {date}",
  "verifications.detail.versionTitle": "Geprüfte Version: Eine Entscheidung gilt nur für diese Version",
  "verifications.banned.title": "Gesperrtes Dokument",
  "verifications.banned.body":
    "Diese Nummer gehört zu einem Dokument, das am {date} gesperrt wurde · Begründung: {reason}. Es kann nie genehmigt werden: Lehnen Sie die Verifizierung ab.",
  "verifications.banned.bodyNoReason":
    "Diese Nummer gehört zu einem gesperrten Dokument. Es kann nie genehmigt werden: Lehnen Sie die Verifizierung ab.",
  "verifications.banned.notTold": "Der Profi erfährt nicht, dass sein Dokument gesperrt ist.",
  "verifications.images.aria": "Ausweisdokumente",
  "verifications.images.show": "Dokumente anzeigen",
  "verifications.images.showAgain": "Erneut anzeigen",
  "verifications.images.loading": "Wird geladen…",
  "verifications.images.notice":
    "Jede Anzeige wird unter Ihrem Namen im Audit-Protokoll erfasst und erscheint im Verlauf des Profis. Die Links laufen nach 2 Minuten ab.",
  "verifications.images.recorded": "Anzeige im Audit-Protokoll erfasst · Links gültig bis {time}",
  "verifications.images.hide": "Verbergen",
  "verifications.images.expired":
    "Die Links sind abgelaufen und die Bilder wurden vom Bildschirm entfernt. Eine erneute Anzeige wird ebenfalls erfasst.",
  "verifications.images.locked": "Ausweisbilder erfordern",
  "verifications.images.deleted": "Die Bilder wurden am {date} gelöscht, wie es die Aufbewahrungsregel verlangt.",
  "verifications.col.field": "Feld",
  "verifications.col.read": "Auf dem Dokument gelesen",
  "verifications.col.typed": "Vom Profi eingegeben",
  "verifications.col.match": "Übereinstimmung",
  "verifications.field.name": "Name",
  "verifications.field.dateOfBirth": "Geboren am",
  "verifications.field.expiresOn": "Gültig bis",
  "verifications.field.documentNumber": "Dokumentnummer",
  "verifications.notAsked": "nicht abgefragt",
  "verifications.match.check": "Auf dem Bild prüfen",
  "verifications.match.same": "Stimmt überein",
  "verifications.match.different": "Stimmt nicht überein",
  "verifications.match.readOnly": "Nur gelesen",
  "verifications.match.expired": "Abgelaufen",
  "verifications.match.valid": "Gültig",
  "verifications.match.banned": "Gesperrt",
  "verifications.match.notBanned": "Nicht gesperrt",
  "verifications.last4Hint":
    "Nur die letzten 4 Zeichen der Nummer werden gespeichert ({last4}): Vergleichen Sie sie mit dem Bild.",
  "verifications.standing.title": "Konto des Profis heute",
  "verifications.standing.verificationStatus": "Verifizierungsstand",
  "verifications.standing.approvalStatus": "Profilfreigabe",
  "verifications.standing.isVerified": "Abzeichen „verifiziert“",
  "verifications.standing.status.unverified": "Nicht verifiziert",
  "verifications.standing.status.pending": "In Prüfung",
  "verifications.standing.status.verified": "Verifiziert",
  "verifications.standing.status.rejected": "Abgelehnt",
  "verifications.standing.approval.pending": "Zu genehmigen",
  "verifications.standing.approval.approved": "Genehmigt",
  "verifications.standing.approval.rejected": "Nicht genehmigt",
  "verifications.yes": "Ja",
  "verifications.no": "Nein",
  "verifications.previous.title": "Frühere Einreichungen",
  "verifications.previous.none": "Erste Einreichung dieses Profis.",
  "verifications.approve.button": "Profi genehmigen",
  "verifications.reject.button": "Ablehnen",
  "verifications.decided": "Über diese Verifizierung wurde bereits entschieden.",
  "verifications.approve.banned":
    "Genehmigung nicht möglich: Die Nummer gehört zu einem gesperrten Dokument. Lehnen Sie die Verifizierung ab.",
  "verifications.approve.locked":
    "Genehmigen erfordert auch APPROVE_PROS_STAFF_FEATURE_ENABLEMENT, die Ihre Rollen nicht enthalten.",
  "verifications.decided.approved": "Genehmigt von {name} am {date}.",
  "verifications.decided.rejected": "Abgelehnt von {name} am {date}.",
  "verifications.decided.reasonCode": "Grund: {reason}",
  "verifications.decided.reason": "Notiz des Teams: „{reason}“",
  "verifications.outcome.approved":
    "{name} ist genehmigt: Das Profil ist verifiziert und kann gefunden und beauftragt werden.",
  "verifications.outcome.rejected":
    "Verifizierung abgelehnt. Der Profi wird in der App benachrichtigt, in seiner Sprache.",
  "verifications.approve.title": "{name} genehmigen",
  "verifications.approve.body":
    "Das Dokument wird akzeptiert und der Profi gilt als verifiziert: Er kann gefunden und beauftragt werden. Die Entscheidung wird mit Ihrer Begründung im Audit-Protokoll erfasst.",
  "verifications.approve.reason": "Begründung (im Audit-Protokoll erfasst)",
  "verifications.approve.hint": "Z. B. „Ausweis lesbar, Selfie passt“.",
  "verifications.approve.submit": "Profi genehmigen",
  "verifications.approve.tooShort": "Geben Sie eine Begründung mit mindestens {min} Zeichen an.",
  "verifications.reject.title": "Verifizierung von {name} ablehnen",
  "verifications.reject.body": "Der Profi kann in der App ein neues Dokument einreichen.",
  "verifications.reject.preset": "Ablehnungsgrund",
  "verifications.reject.presetNone": "Begründung wählen…",
  "verifications.reject.code.photo_unreadable": "Foto unleserlich",
  "verifications.reject.code.selfie_mismatch": "Selfie passt nicht",
  "verifications.reject.code.document_expired": "Dokument abgelaufen",
  "verifications.reject.code.document_not_accepted": "Dokument nicht akzeptiert",
  "verifications.reject.code.other": "Sonstiges",
  "verifications.reject.reason": "Notiz für das Team (optional)",
  "verifications.reject.noteHint":
    "Wird dem Profi nie gezeigt: Er erhält in der App die Nachricht zum gewählten Grund, in seiner Sprache.",
  "verifications.reject.submit": "Verifizierung ablehnen",
  "verifications.reject.pickCode": "Wählen Sie den Ablehnungsgrund.",
  "verifications.reason.tooLong": "Die Begründung darf höchstens {max} Zeichen haben.",
  "verifications.reason.count": "{count} von {max} Zeichen",
  "verifications.dialog.version": "Entscheidung über Version {version}",
  "verifications.dialog.cancel": "Abbrechen",
  "verifications.dialog.sending": "Wird gesendet…",
  "verifications.error.stale":
    "Diese Verifizierung hat sich geändert, während Sie sie angesehen haben (vielleicht hat jemand anderes entschieden). Laden Sie neu, um die aktuelle Version zu sehen, und entscheiden Sie erneut.",
  "verifications.error.approveRefused":
    "Genehmigung abgelehnt: Ihre Rollen enthalten REVIEW_VERIFICATIONS und APPROVE_PROS nicht, oder das Dokument wurde gerade gesperrt. Laden Sie neu.",
  "verifications.error.rejectRefused":
    "Ablehnen nicht möglich: Ihre Rollen enthalten REVIEW_VERIFICATIONS nicht mehr. Bitten Sie eine verantwortliche Person um Zugang.",
  "verifications.error.imagesRefused":
    "Bilder verweigert: Ihre Rollen enthalten VIEW_ID_IMAGES nicht mehr. Dieser Versuch wird im Audit-Protokoll erfasst.",
  "verifications.error.approveInvalid":
    "Genehmigung abgelehnt: Die Verifizierung ist bereits entschieden, das Dokument ist gesperrt oder die Begründung ist ungültig. Laden Sie neu und prüfen Sie.",
  "verifications.error.rejectInvalid":
    "Ablehnung abgewiesen: Die Verifizierung ist bereits entschieden, oder die Notiz hat mehr als {max} Zeichen.",
  "verifications.error.imagesDeleted":
    "Die Bilder sind nicht mehr verfügbar: Sie wurden nach der Aufbewahrungsregel gelöscht, oder die Verifizierung wurde nicht gefunden.",
  "verifications.error.outcomeUnknown":
    "Keine Antwort vom Server: Die Entscheidung wurde möglicherweise gespeichert. Versuchen Sie es erneut: Derselbe Klick kann nicht zweimal ausgeführt werden.",
};
